import React from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import GatePass from '../components/GatePass';

// Store Inward — the STORE books QUANTITY received (qty only). Quality pass/fail stays
// with QC on their Q&Q tab. No quality control appears here (backend also has no quality
// field on /goods-receipt). Awaiting-QC + Return-to-Vendor shown for visibility.
// Item Master tab (Phase 1, mig 123): the store-maintained general-store catalogue.
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export default function StoreInward() {
  const { user } = useAuth();
  const isGate = user?.role === 'gate';
  const [tab, setTab] = React.useState('inward');

  // ── THE REJECTIONS COUNT, ON THE TAB ────────────────────────────────────────────────────
  // The register, the ageing and both write routes have existed since mig 203 and work: every
  // open rejection is ADMITTED by POST /grn/:id/disposition today. What was missing is that
  // nothing said there were any. The tab read "Rejections" whether the queue held nothing or
  // 3,821 units, so the only way to learn was to go looking — and nobody went looking.
  //
  // Counted at the parent because a badge has to be visible from a tab the user is NOT on.
  // Fails silently: a broken count must never take the store screen down with it.
  const [rejCount, setRejCount] = React.useState(0);
  React.useEffect(() => {
    api.get('/procurement/rejections')
      .then(r => setRejCount((r.data.data || []).filter(x => Number(x.undisposed_qty) > 0).length))
      .catch(() => {});
  }, []);

  const tabBtn = (id, label, badge = 0) => (
    <button onClick={() => setTab(id)} style={{
      padding: '9px 18px', border: 'none', borderBottom: tab === id ? '3px solid #3B3A8F' : '3px solid transparent',
      background: 'transparent', color: tab === id ? '#3B3A8F' : '#888', fontWeight: tab === id ? 700 : 500,
      fontSize: 14, cursor: 'pointer' }}>
      {label}
      {badge > 0 && (
        <span style={{ marginLeft: 6, background: '#A32D2D', color: '#fff', borderRadius: 10,
                       padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{badge}</span>
      )}
    </button>
  );
  // Gate-security logins see ONLY the gate-pass exit queue — nothing else in the store module.
  if (isGate) {
    return (
      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        <h2 style={{ margin: '0 0 12px', color: '#1a1a2e' }}>🚪 Gate</h2>
        <GatePass user={user} />
      </div>
    );
  }
  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 12px', color: '#1a1a2e' }}>📥 Store</h2>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 18 }}>
        {tabBtn('inward', 'Inward')}
        {tabBtn('issues', 'Issue Slip')}
        {tabBtn('returns', 'Returns')}
        {tabBtn('onhand', 'Stock on Hand')}
        {tabBtn('consumption', 'GPO Consumption')}
        {tabBtn('indents', 'Indents')}
        {tabBtn('items', 'Item Master')}
        {tabBtn('mybills', 'My Bills')}
        {tabBtn('pos', 'POs')}
        {tabBtn('rejections', 'Rejections', rejCount)}
        {tabBtn('gatepass', 'Gate Pass')}
      </div>
      {tab === 'inward' ? <Inward /> : tab === 'issues' ? <IssueSlips /> : tab === 'returns' ? <StoreReturns /> : tab === 'onhand' ? <StockOnHand /> : tab === 'consumption' ? <GpoConsumption /> : tab === 'indents' ? <Indents user={user} /> : tab === 'items' ? <ItemMaster /> : tab === 'mybills' ? <MyBills /> : tab === 'pos' ? <PurchaseOrders /> : tab === 'rejections' ? <Rejections user={user} /> : <GatePass user={user} />}
    </div>
  );
}

function Inward() {
  const { user } = useAuth();
  const canEditGrn = ['stores_incharge', 'admin'].includes(user?.role);   // ruling 2 — admin NOT excluded
  // Byte-identical to the MGMT test in POST /receipt (mig 201). Only these two may permit a
  // 4th or later delivery; everyone else gets a refusal naming what would let it through.
  const canApprove = ['admin', 'management'].includes(user?.role);
  const [rows, setRows] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [modal, setModal] = React.useState(null);      // multi-line receipt modal, or null
  const [editGrn, setEditGrn] = React.useState(null);  // GRN-edit modal state, or null
  const [recvLines, setRecvLines] = React.useState([]); // selected supplier's receivable PO rows
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState('');
  const [poSearch, setPoSearch] = React.useState('');    // client-side filter — PO No
  const [gpoSearch, setGpoSearch] = React.useState('');  // client-side filter — GPO
  const [rejectedPos, setRejectedPos] = React.useState([]); // read-only: to EXPLAIN a searched-but-rejected PO
  const load = React.useCallback(() => { api.get('/procurement/awaiting-receipt').then(r => setRows(r.data.data || [])).catch(() => {}); }, []);
  React.useEffect(() => {
    load();
    api.get('/suppliers').then(r => setSuppliers((r.data.data || []).filter(s => s.is_active !== false))).catch(() => {});
    // Rejected POs are NOT receivable — fetched only to explain a "no match" search, never to receive.
    api.get('/procurement/pos?status=rejected').then(r => setRejectedPos(r.data.data || [])).catch(() => {});
  }, [load]);

  const loadRecv = (sid) => { if (!sid) { setRecvLines([]); return; } api.get(`/procurement/receivable-lines?supplier=${sid}`).then(r => setRecvLines(r.data.data || [])).catch(() => setRecvLines([])); };
  const blankLine = () => ({ po_id: '', po_line_id: null, size: '', invoiced_qty: '', received_qty: '' });
  // gst_pct is OPTIONAL and blank by default (mig 196) — NOT pre-set to 5. The store keys the
  // vendor's tax-inclusive total and often has no breakdown; a default would put a number the
  // store never read onto a finance record. Blank sends nothing and the column stays NULL.
  // Keyed by po_id, not a single string: one receipt can span POs at different delivery
  // counts, and the reason is a fact about a PO rather than about the delivery van.
  const blankHdr = { supplier_id: '', invoice_no: '', invoice_date: '', challan_no: '', challan_date: '', received_date: '', invoice_amount: '', gst_pct: '', gate_entry_no: '', vehicle_no: '', exc: {}, appr: {}, over: {} };
  // What to pre-fill as received. `remaining` is a PO-LEVEL figure and stays that way — the
  // tolerance is an allowance against the order, not against each item — but pre-filling the
  // whole PO's remaining onto ONE item of a two-item PO would invite booking 2,200 labels where
  // only 1,100 were ordered. On a multi-line PO the line's own quantity is the honest default.
  const defaultRecv = (row) => (Number(row.line_count || 0) > 1 && row.line_qty != null)
    ? String(num(row.line_qty)) : String(num(row.remaining));
  const openMulti = () => { setErr(''); setRecvLines([]); setModal({ ...blankHdr, lines: [blankLine()] }); };
  const openReceive = (po) => { setErr(''); loadRecv(po.supplier_id); setModal({ ...blankHdr, supplier_id: po.supplier_id || '', // invoiced_qty opens BLANK while received_qty is pre-filled from the PO. Seeding both made
    // every line read "billed == received" by default, which is the ambiguity this pass removes.
    lines: [{ po_id: po.id, po_line_id: po.po_line_id || null, size: '', invoiced_qty: '', received_qty: defaultRecv(po) }] }); };
  const onSupplier = (sid) => { setModal(m => ({ ...m, supplier_id: sid, lines: [blankLine()] })); loadRecv(sid); };
  const setLine = (i, patch) => setModal(m => ({ ...m, lines: m.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const addLine = () => setModal(m => ({ ...m, lines: [...m.lines, blankLine()] }));
  const addSize = (i) => setModal(m => ({ ...m, lines: [...m.lines.slice(0, i + 1), { ...m.lines[i], size: '', invoiced_qty: '', received_qty: '' }, ...m.lines.slice(i + 1)] }));
  const rmLine = (i) => setModal(m => ({ ...m, lines: m.lines.length > 1 ? m.lines.filter((_, j) => j !== i) : m.lines }));
  // ONE ROW PER PO LINE (mig 266). recvLines now holds a row per purchase_order_lines row, so
  // several rows share a PO id — look up by the LINE key the dropdown sends, and only fall back
  // to the PO id for a PO with no lines.
  const rowKey = (x) => x.po_line_id || x.id;
  // Takes the modal LINE. po_id keeps meaning the purchase order throughout this file — the
  // delivery-tier and over-receipt reasons are keyed by it and the backend groups on it — so the
  // line identity rides alongside in po_line_id rather than overloading po_id.
  const poOf = (l) => {
    const key = (l && typeof l === 'object') ? l : { po_id: l };
    return (key.po_line_id && recvLines.find(x => x.po_line_id === key.po_line_id))
        || recvLines.find(x => x.id === key.po_id) || {};
  };

  const submit = async () => {
    const m = modal;
    if (!m.supplier_id) { setErr('Pick a supplier'); return; }
    if (!m.invoice_no.trim()) { setErr('Invoice no is required'); return; }
    if (!m.invoice_date) { setErr('Invoice date is required'); return; }
    if (!m.challan_no.trim()) { setErr('Challan / DC no is required'); return; }
    if (!m.challan_date) { setErr('Challan / DC date is required'); return; }
    // THE ARRIVAL DATE (mig 320). The challan date is the supplier's dispatch date; this is when
    // the goods reached us, and it is what every delivery score is supposed to measure. It was
    // never asked for, and grn.received_date defaulted to CURRENT_DATE behind the silence.
    if (!m.received_date) { setErr('Arrival date is required — the day the goods reached us, not today'); return; }
    if (!(num(m.invoice_amount) > 0)) { setErr('Invoice amount must be > 0'); return; }
    const clean = m.lines.filter(l => l.po_id && num(l.received_qty) > 0);
    if (!clean.length) { setErr('Add at least one line with a received qty'); return; }
    // Mirrors the server's 400 so the clerk is told here rather than after a round trip. The
    // billed quantity is very often the same as the received one — but it has to be entered,
    // because a defaulted copy is indistinguishable from a deliberate one once it is stored.
    const missingInv = clean.findIndex(l => !(num(l.invoiced_qty) > 0));
    if (missingInv >= 0) {
      setErr(`Line ${missingInv + 1}: enter the Invoiced qty — what the BILL says for this item. `
           + `Often the same as Received, but it must be entered, not assumed.`);
      return;
    }
    // Mirrors the server's tiers (mig 201) so the clerk learns here, not from a 409. The
    // SERVER remains the authority — this only saves a round trip.
    for (const poId of [...new Set(clean.map(l => l.po_id))]) {
      const po = poOf(poId); const seq = num(po.grn_count) + 1;
      if (seq >= 3 && !String(m.exc?.[poId] || '').trim()) { setErr(`${po.po_no}: delivery ${seq} needs a reason.`); return; }
      if (seq >= 4 && !String(m.appr?.[poId] || '').trim()) { setErr(`${po.po_no}: delivery ${seq} needs a management approval reason.`); return; }
      if (seq >= 4 && !canApprove) { setErr(`${po.po_no}: delivery ${seq} can only be booked by admin or management.`); return; }
    }
    // TOLERANCE (mig 222/223). The ceiling is the ORDER plus its material tolerance, and it is
    // measured on the PO's TOTAL for this receipt, not line by line — a PO split across three
    // size lines was previously checked one line at a time, so three lines each under the
    // remaining could sum to well over it and pass.
    for (const poId of [...new Set(clean.map(l => l.po_id))]) {
      const po = poOf(poId);
      const total = clean.filter(l => l.po_id === poId).reduce((t, l) => t + num(l.received_qty), 0);
      const ceiling = po.tolerance_remaining != null ? num(po.tolerance_remaining) : num(po.remaining);
      if (total > ceiling + 0.0005) {
        setErr(`${po.po_no}: received ${total} exceeds ${ceiling.toFixed(3)} — remaining ${num(po.remaining)}`
          + `${num(po.tolerance_pct) ? ` plus the ${num(po.tolerance_pct)}% ${po.material_type} tolerance` : ''}.`); return;
      }
      // Inside the band. Accepted, but not silently — same rule the server enforces.
      if (total > num(po.remaining) + 0.0005 && !String(m.over?.[poId] || '').trim()) {
        setErr(`${po.po_no}: ${total} is over the ordered quantity (within the ${num(po.tolerance_pct)}% tolerance). A reason is required.`); return;
      }
    }
    setBusy('rec'); setErr('');
    const payload = {
        supplier_id: m.supplier_id, invoice_no: m.invoice_no, invoice_date: m.invoice_date,
        challan_no: m.challan_no, challan_date: m.challan_date, received_date: m.received_date, invoice_amount: num(m.invoice_amount),
        ...(String(m.gst_pct).trim() !== '' ? { gst_pct: num(m.gst_pct) } : {}),
        // Both OPTIONAL, both omitted when blank rather than sent as ''. The gate link is an
        // improvement to reach for, never a condition of booking goods in — mig 199 keeps the
        // column nullable for exactly this, and no validation above gates on either.
        ...(m.gate_entry_no.trim() ? { gate_entry_no: m.gate_entry_no.trim() } : {}),
        ...(m.vehicle_no.trim() ? { vehicle_no: m.vehicle_no.trim() } : {}),
        // Only the POs that actually need one — sending empty strings would look like a
        // reason was given for every line.
        delivery_exceptions: Object.fromEntries(Object.entries(m.exc || {}).filter(([, v]) => String(v).trim()).map(([k, v]) => [k, String(v).trim()])),
        management_approvals: Object.fromEntries(Object.entries(m.appr || {}).filter(([, v]) => String(v).trim()).map(([k, v]) => [k, String(v).trim()])),
        over_receipt_reasons: Object.fromEntries(Object.entries(m.over || {}).filter(([, v]) => String(v).trim()).map(([k, v]) => [k, String(v).trim()])),
        // NO `|| received_qty` FALLBACK. Copying the received count into the billed column made
        // "the bill agrees with the goods" indistinguishable from "nobody filled it in", and
        // every check over invoiced_qty inherited that ambiguity. It is validated above instead.
        lines: clean.map(l => ({ po_id: l.po_id, po_line_id: l.po_line_id || null, size: l.size, invoiced_qty: num(l.invoiced_qty), received_qty: num(l.received_qty) })),
    };
    // TWO WARNINGS, EITHER OR BOTH. A 409 carrying a *_warning flag is not a failure — it is
    // the server asking a question, and there are now two it can ask:
    //   qty_variance_warning   — this LINE's bill and count disagree by more than 2%
    //   claim_variance_warning — the bill's TOTAL does not match the goods on it at PO rate
    // They are independent and the server checks quantity first, so answering one can surface
    // the other. The loop accumulates confirmations rather than nesting try/catch per warning,
    // which is what let the second question go unasked when the first was answered.
    // Same contract as the Day Book duplicate warning: show what was found, and send the
    // confirm flag only on an explicit yes.
    const WARNINGS = [
      ['qty_variance_warning', 'confirm_qty_variance'],
      ['claim_variance_warning', 'confirm_claim_variance'],
    ];
    const send = (extra) => api.post('/procurement/receipt', { ...payload, ...extra });
    try {
      const confirms = {};
      for (;;) {
        try { await send(confirms); break; }
        catch (ex) {
          const d = ex.response?.data;
          const hit = ex.response?.status === 409 && WARNINGS.find(([flag]) => d?.[flag]);
          if (!hit) throw ex;
          if (!window.confirm(`${d.error}\n\nSave this receipt anyway?`)) { setBusy(''); return; }
          confirms[hit[1]] = true;
        }
      }
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Receipt failed'); } finally { setBusy(''); }
  };
  const markReturned = async (po) => {
    if (!window.confirm(`Confirm goods for ${po.po_no} (GPO ${po.gpo_no}) returned to vendor?`)) return;
    setBusy(po.id);
    try { await api.post(`/procurement/goods-receipt/${po.id}/mark-returned`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); } finally { setBusy(''); }
  };

  // GRN edit (correct a mis-keyed receipt before it locks). Both locks are enforced ABSOLUTELY
  // by the backend; the button only shows for QC-pending rows (Lock A open) to stores_incharge/admin.
  const openEdit = (p) => setEditGrn({
    grn_id: p.grn_id, grn_no: p.grn_no, po_no: p.po_no,
    received_qty: String(num(p.received_qty)), rejected_qty: String(num(p.rejected_qty)),
    remarks: p.grn_remarks || '', challan_no: p.challan_no || '',
    challan_date: p.challan_date || '', received_date: p.received_date || '', reason: '', err: '',
  });
  const submitEdit = async () => {
    const e = editGrn;
    if (!e.reason.trim()) { setEditGrn(m => ({ ...m, err: 'Reason is required' })); return; }
    if (!(num(e.received_qty) > 0)) { setEditGrn(m => ({ ...m, err: 'Received qty must be > 0' })); return; }
    if (num(e.rejected_qty) < 0 || num(e.rejected_qty) > num(e.received_qty)) { setEditGrn(m => ({ ...m, err: 'Rejected must be between 0 and received' })); return; }
    setBusy('edit');
    try {
      await api.patch(`/procurement/grn/${e.grn_id}`, {
        received_qty: num(e.received_qty), rejected_qty: num(e.rejected_qty),
        remarks: e.remarks, challan_no: e.challan_no,
        challan_date: e.challan_date || null, received_date: e.received_date || null,
        reason: e.reason.trim(),
      });
      setEditGrn(null); load();
    } catch (err) { setEditGrn(m => ({ ...m, err: err.response?.data?.error || 'Edit failed' })); } finally { setBusy(''); }
  };

  // Receivable = up to 2 deliveries AND still something to receive (received-so-far < ordered).
  // The delivery count no longer hides a PO (mig 201). A PO that has taken two deliveries
  // stays listed and carries its count, so a clerk holding a third sees "2 deliveries taken"
  // rather than a PO that has silently vanished.
  const toReceive = rows.filter(r => num(r.received_so_far) < num(r.quantity) - 0.0005);
  // Client-side live filter of the already-loaded list — PO No + GPO (case-insensitive, partial, AND).
  const isFiltered = !!(poSearch.trim() || gpoSearch.trim());
  const shownReceive = toReceive.filter(p =>
    (!poSearch.trim() || String(p.po_no || '').toLowerCase().includes(poSearch.trim().toLowerCase())) &&
    (!gpoSearch.trim() || String(p.gpo_no || '').toLowerCase().includes(gpoSearch.trim().toLowerCase()))
  );
  // When a search matches nothing receivable, see if it matches a REJECTED PO — to explain the absence.
  const matchedRejected = !isFiltered ? [] : rejectedPos.filter(p =>
    (!poSearch.trim() || String(p.po_no || '').toLowerCase().includes(poSearch.trim().toLowerCase())) &&
    (!gpoSearch.trim() || String(p.gpo_no || '').toLowerCase().includes(gpoSearch.trim().toLowerCase()))
  );
  const awaitingQC = rows.filter(r => r.grn_no && r.quality_status === 'pending');
  const returnPend = rows.filter(r => r.quality_status === 'failed' && r.return_pending);

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <>
      <div style={{ marginBottom: 18, padding: '9px 13px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
        Book <strong>one vendor invoice covering multiple items</strong> in a single receipt. Each line books its own GRN (QC per item); the invoice goes to <strong>Finance</strong> after QC. A PO item may arrive across <strong>2 deliveries</strong>.
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>Awaiting Receipt ({isFiltered ? `${shownReceive.length} of ${toReceive.length}` : toReceive.length})</div>
          <button onClick={openMulti} style={{ padding: '7px 16px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>📥 Record Receipt (multi-item)</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input value={poSearch} onChange={e => setPoSearch(e.target.value)} placeholder="🔍 Search PO No" style={{ flex: 1, maxWidth: 260, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
          <input value={gpoSearch} onChange={e => setGpoSearch(e.target.value)} placeholder="🔍 Search GPO" style={{ flex: 1, maxWidth: 260, padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
          {isFiltered && <button onClick={() => { setPoSearch(''); setGpoSearch(''); }} style={{ padding: '7px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#555' }}>Clear</button>}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['PO No', 'GPO', 'Type', 'Item', 'Ordered', 'Received', 'Remaining', 'Supplier', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {shownReceive.length === 0 && matchedRejected.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{isFiltered ? 'No matches — adjust the search.' : 'Nothing awaiting receipt.'}</td></tr>}
            {shownReceive.length === 0 && matchedRejected.slice(0, 5).map(p => (
              <tr key={`rej-${p.id}`}><td colSpan={9} style={{ ...td, background: '#FCEBEB', color: '#A32D2D', fontSize: 12 }}>
                ⛔ <strong style={{ fontFamily: 'monospace' }}>{p.po_no}</strong>{p.gpo_no ? ` (GPO ${p.gpo_no})` : ''} was <strong>rejected</strong>{p.rejection_reason ? ` ("${p.rejection_reason}")` : ''} — not available to receive.
              </td></tr>
            ))}
            {shownReceive.length === 0 && matchedRejected.length > 5 && <tr><td colSpan={9} style={{ ...td, color: '#A32D2D', fontSize: 11 }}>…and {matchedRejected.length - 5} more rejected.</td></tr>}
            {shownReceive.map(p => (
              <tr key={p.id}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{p.po_no}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#E22D2D' }}>{p.gpo_no || '—'}</td>
                <td style={td}>{p.material_type}</td>
                <td style={td}>{p.description || '—'}</td>
                <td style={td}>{num(p.quantity).toLocaleString('en-IN')} {p.unit}</td>
                <td style={td}>{num(p.received_so_far).toLocaleString('en-IN')}
                  {num(p.grn_count) > 0 && <span style={{ marginLeft: 5, fontSize: 11, color: num(p.grn_count) >= 2 ? '#B45309' : '#aaa', fontWeight: num(p.grn_count) >= 2 ? 700 : 400 }}>
                    ({num(p.grn_count)}{num(p.grn_count) >= 2 ? ' taken' : ''})</span>}</td>
                <td style={{ ...td, fontWeight: 600, color: num(p.received_so_far) > 0 ? '#B45309' : '#555' }}>{num(p.remaining).toLocaleString('en-IN')}</td>
                <td style={td}>{p.supplier_name || '—'}</td>
                <td style={td}><button onClick={() => openReceive(p)} style={{ padding: '6px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>📥 Receive</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Received — Awaiting QC ({awaitingQC.length})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['GRN', 'Gate entry', 'PO No', 'GPO', 'Received', 'Rejected', 'Quality', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {awaitingQC.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa' }}>None awaiting QC.</td></tr>}
            {awaitingQC.map(p => (
              <tr key={p.id}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#166534' }}>{p.grn_no}</td>
                {/* Resolved through the FK (mig 199), never the text. Blank means no register
                    number was keyed — not that the goods never crossed the gate. */}
                <td style={{ ...td, fontFamily: 'monospace', color: p.gate_entry_no ? '#9A6B00' : '#ccc' }}>{p.gate_entry_no || '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{p.po_no}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#E22D2D' }}>{p.gpo_no || '—'}</td>
                <td style={td}>{num(p.received_qty).toLocaleString('en-IN')}</td>
                <td style={td}>{num(p.rejected_qty).toLocaleString('en-IN')}</td>
                <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, background: '#FEF3E2', color: '#B45309' }}>pending QC</span></td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {canEditGrn && p.grn_id && (
                    <button onClick={() => openEdit(p)} style={{ padding: '4px 12px', background: '#EEF0F7', border: '1px solid #C9CEE6', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#3B3A8F', cursor: 'pointer' }}>Edit</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEditGrn && awaitingQC.length > 0 && (
          <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>Edit corrects a mis-keyed receipt before it locks. Locks once QC is decided or a certified invoice references it.</div>
        )}
      </div>

      {returnPend.length > 0 && (
        <div style={{ ...card, border: '1px solid #F3C7C7' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>QC Failed — Return to Vendor ({returnPend.length})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['GRN', 'PO No', 'GPO', 'Item', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {returnPend.map(p => (
                <tr key={p.id}>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#A32D2D' }}>{p.grn_no}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{p.po_no}</td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#E22D2D' }}>{p.gpo_no || '—'}</td>
                  <td style={td}>{p.description || '—'}</td>
                  <td style={td}><button disabled={busy === p.id} onClick={() => markReturned(p)} style={{ padding: '6px 14px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>{busy === p.id ? '…' : '↩ Mark Returned'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (() => {
        const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };
        const sumLines = modal.lines.reduce((s, l) => { const p = poOf(l); return s + num(l.invoiced_qty) * num(p.display_rate ?? p.unit_price); }, 0);
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 900, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px' }}>Record Receipt — multi-item</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>One vendor invoice, multiple items. Each line books its own GRN (QC per item). Not a payable until Finance certifies after QC.</p>
            {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Supplier *</label>
              <select value={modal.supplier_id} onChange={e => onSupplier(e.target.value)} style={{ ...inp, maxWidth: 360 }}>
                <option value="">— pick a supplier —</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            {/* The gate register (mig 199). OPTIONAL — unlike the gate-pass return, where the
                form exists to record a movement, this form exists to book goods: a clerk without
                the guard's register to hand must still be able to receive. Placed in its own
                callout rather than beside Challan no because the number operators reach for by
                mistake IS the challan, and a quiet sixth box in that grid invites the confusion
                it is meant to prevent. */}
            <div style={{ marginBottom: 14, padding: 12, background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label style={{ ...lbl, color: '#9A6B00' }}>Gate entry number</label>
                <input value={modal.gate_entry_no} onChange={e => setModal(m => ({ ...m, gate_entry_no: e.target.value }))}
                  style={inp} placeholder="IN-entry number from the gate register" />
                <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 4 }}>
                  From the security guard's register — not the challan number, which is the vendor's.
                </div>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ ...lbl, color: '#9A6B00' }}>Vehicle no</label>
                <input value={modal.vehicle_no} onChange={e => setModal(m => ({ ...m, vehicle_no: e.target.value }))}
                  style={inp} placeholder="e.g. MH-01-AB-1234" />
                <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 4 }}>Optional — recorded against the gate entry.</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #eee', margin: '4px 0 10px', paddingTop: 10, fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>Vendor Invoice (required)</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 130px' }}><label style={lbl}>Invoice no *</label><input value={modal.invoice_no} onChange={e => setModal(m => ({ ...m, invoice_no: e.target.value }))} style={inp} /></div>
              <div style={{ flex: '1 1 130px' }}><label style={lbl}>Invoice date *</label><input type="date" value={modal.invoice_date} onChange={e => setModal(m => ({ ...m, invoice_date: e.target.value }))} style={inp} /></div>
              <div style={{ flex: '1 1 130px' }}><label style={lbl}>Challan / DC no *</label><input value={modal.challan_no} onChange={e => setModal(m => ({ ...m, challan_no: e.target.value }))} style={inp} /></div>
              <div style={{ flex: '1 1 130px' }}><label style={lbl}>Challan / DC date *</label><input type="date" value={modal.challan_date} onChange={e => setModal(m => ({ ...m, challan_date: e.target.value }))} style={inp} /></div>
              <div style={{ flex: '1 1 130px' }}><label style={lbl}>Arrival date *</label><input type="date" value={modal.received_date} onChange={e => setModal(m => ({ ...m, received_date: e.target.value }))} style={inp} />
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>When the goods reached us</div></div>
              <div style={{ flex: '1 1 150px' }}><label style={lbl}>Invoice amount * <span style={{ color: '#aaa', fontWeight: 400 }}>(vendor's claim)</span></label><input type="number" step="0.01" value={modal.invoice_amount} onChange={e => setModal(m => ({ ...m, invoice_amount: e.target.value }))} style={inp} /></div>
              {/* Optional. Left blank the slab is recorded as UNSTATED, not zero — Finance's
                  reconciliation already infers it from claim ÷ line value, and a guess here
                  would look like the vendor declared one. */}
              <div style={{ flex: '1 1 120px' }}><label style={lbl}>GST % <span style={{ color: '#aaa', fontWeight: 400 }}>(if on the bill)</span></label>
                <select value={modal.gst_pct} onChange={e => setModal(m => ({ ...m, gst_pct: e.target.value }))} style={inp}>
                  <option value="">— not stated —</option>
                  {[0, 0.25, 3, 5, 12, 18, 28].map(g => <option key={g} value={g}>{g}%</option>)}
                </select></div>
            </div>
            <div style={{ borderTop: '1px solid #eee', margin: '4px 0 8px', paddingTop: 10, fontSize: 11, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>Lines</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
              <thead><tr>{['PO item', 'Size', 'Ordered', 'Recv-so-far', 'Remaining', 'Invoiced *', 'Received *', 'Rate', 'Line ₹', ''].map(h => <th key={h} style={{ ...th, padding: '6px 6px' }}>{h}</th>)}</tr></thead>
              <tbody>
                {modal.lines.map((l, i) => { const po = poOf(l); return (
                  <tr key={i}>
                    <td style={{ ...td, padding: '5px 6px', minWidth: 190 }}>
                      <select value={l.po_line_id || l.po_id}
                        onChange={e => { const row = recvLines.find(x => rowKey(x) === e.target.value) || {};
                          setLine(i, { po_id: row.id || '', po_line_id: row.po_line_id || null,
                            received_qty: row.id ? defaultRecv(row) : '' }); }}
                        style={{ ...inp, padding: '6px 8px' }} disabled={!modal.supplier_id}>
                        <option value="">— pick —</option>
                        {recvLines.map(x => <option key={rowKey(x)} value={rowKey(x)}>
                          {x.po_no} · {x.display_desc || x.description || x.material_type}
                          {num(x.line_count) > 1 ? ` — ${num(x.line_qty).toLocaleString('en-IN')} @ ₹${num(x.display_rate)}` : ''}
                          {num(x.grn_count) >= 2 ? ` — ${num(x.grn_count)} deliveries taken` : ''}
                        </option>)}
                      </select>
                    </td>
                    <td style={{ ...td, padding: '5px 6px', width: 70 }}><input value={l.size} onChange={e => setLine(i, { size: e.target.value })} style={{ ...inp, padding: '6px 8px' }} placeholder="—" /></td>
                    <td style={{ ...td, padding: '5px 6px', color: '#888' }}>{po.id ? num(po.ordered_qty).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ ...td, padding: '5px 6px', color: '#888' }}>{po.id ? num(po.received_so_far).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ ...td, padding: '5px 6px', fontWeight: 600 }}>{po.id ? num(po.remaining).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ ...td, padding: '5px 6px', width: 80 }}><input type="number" step="0.001" value={l.invoiced_qty} onChange={e => setLine(i, { invoiced_qty: e.target.value })} style={{ ...inp, padding: '6px 8px' }} /></td>
                    <td style={{ ...td, padding: '5px 6px', width: 80 }}><input type="number" step="0.001" value={l.received_qty} onChange={e => setLine(i, { received_qty: e.target.value })} style={{ ...inp, padding: '6px 8px', borderColor: !po.id ? '#ddd'
                      // Red is REFUSAL, amber is "allowed but you will be asked why". Reddening
                      // the band too would say the receipt is impossible when it is not.
                      : num(l.received_qty) > num(po.tolerance_remaining ?? po.remaining) + 0.0005 ? '#E22D2D'
                      : num(l.received_qty) > num(po.remaining) + 0.0005 ? '#B45309' : '#ddd' }} /></td>
                    <td style={{ ...td, padding: '5px 6px', color: po.rate_is_blended ? '#B45309' : '#888' }}
                        title={po.rate_is_blended ? 'This PO has several items and no line rate — this is their average, not a price either item carries.' : undefined}>
                      {po.id ? num(po.display_rate ?? po.unit_price).toFixed(2) : '—'}{po.rate_is_blended ? ' ⚠' : ''}</td>
                    <td style={{ ...td, padding: '5px 6px' }}>₹{(num(l.invoiced_qty) * num(po.display_rate ?? po.unit_price)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td style={{ ...td, padding: '5px 6px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => addSize(i)} title="Add a size for this item" style={{ background: 'transparent', border: '1px solid #ddd', borderRadius: 5, color: '#3B3A8F', cursor: 'pointer', fontSize: 11, padding: '3px 6px', marginRight: 4 }}>＋size</button>
                      {modal.lines.length > 1 && <button onClick={() => rmLine(i)} style={{ background: 'transparent', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16 }}>×</button>}
                    </td>
                  </tr>
                ); })}
              </tbody>
            </table>
            {/* Delivery exceptions (mig 201). Appears only for the POs that need it, and only
                once a line actually selects them — a reason box on every receipt would train
                people to type something to make it go away. */}
            {[...new Set(modal.lines.filter(l => l.po_id).map(l => l.po_id))]
              .map(poId => ({ poId, po: poOf(poId), seq: num(poOf(poId).grn_count) + 1 }))
              .filter(x => x.seq >= 3)
              .map(({ poId, po, seq }) => (
                <div key={poId} style={{ marginBottom: 12, padding: 12, background: seq >= 4 ? '#FCEBEB' : '#FBF7EF', border: `1px solid ${seq >= 4 ? '#f3c7c7' : '#f6d195'}`, borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: seq >= 4 ? '#A32D2D' : '#9A6B00', marginBottom: 6 }}>
                    {po.po_no} — delivery {seq}
                    {seq >= 4 && <span style={{ marginLeft: 6, fontWeight: 500 }}>· refused without a management approval</span>}
                  </div>
                  <label style={{ ...lbl, color: seq >= 4 ? '#A32D2D' : '#9A6B00' }}>Reason for this delivery *</label>
                  <input value={modal.exc?.[poId] || ''} onChange={e => setModal(m => ({ ...m, exc: { ...m.exc, [poId]: e.target.value } }))}
                    style={inp} placeholder="What arrived, and why there is a further delivery" />
                  {seq >= 4 && (
                    <div style={{ marginTop: 8 }}>
                      <label style={{ ...lbl, color: '#A32D2D' }}>Management approval — reason *</label>
                      <input value={modal.appr?.[poId] || ''} onChange={e => setModal(m => ({ ...m, appr: { ...m.appr, [poId]: e.target.value } }))}
                        style={inp} disabled={!canApprove}
                        placeholder={canApprove ? 'Why this exception is permitted' : 'Admin or management only'} />
                      <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>
                        {canApprove
                          ? 'Recorded separately from the reason above, against your name.'
                          : 'You cannot approve this. An admin or management user must book this receipt.'}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            {/* OVER-RECEIPT inside tolerance (mig 222/223). Appears only for a PO whose lines
                actually sum past its ordered quantity — never pre-emptively, for the same
                reason the delivery box does not: a reason field shown on every receipt teaches
                people to type something to dismiss it. */}
            {[...new Set(modal.lines.filter(l => l.po_id).map(l => l.po_id))]
              .map(poId => {
                const po = poOf(poId);
                const total = modal.lines.filter(l => l.po_id === poId).reduce((t, l) => t + num(l.received_qty), 0);
                return { poId, po, total, over: +(total - num(po.remaining)).toFixed(3) };
              })
              .filter(x => x.over > 0.0005 && x.total <= num(x.po.tolerance_remaining ?? x.po.remaining) + 0.0005)
              .map(({ poId, po, total, over }) => (
                <div key={`over-${poId}`} style={{ marginBottom: 12, padding: 12, background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#9A6B00', marginBottom: 6 }}>
                    {po.po_no} — {total.toLocaleString('en-IN')} against {num(po.remaining).toLocaleString('en-IN')} outstanding
                    <span style={{ fontWeight: 500 }}> · {over.toLocaleString('en-IN')} over, inside the {num(po.tolerance_pct)}% {po.material_type} tolerance</span>
                  </div>
                  <label style={{ ...lbl, color: '#9A6B00' }}>Reason for accepting the excess *</label>
                  <input value={modal.over?.[poId] || ''} onChange={e => setModal(m => ({ ...m, over: { ...m.over, [poId]: e.target.value } }))}
                    style={inp} placeholder="Why the extra quantity is being taken in" />
                  <div style={{ fontSize: 11, color: '#9A6B00', marginTop: 4 }}>
                    The tolerance in force today and the ceiling it was measured against are recorded on this GRN, so the receipt stays readable if the tolerance changes later.
                  </div>
                </div>
              ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <button onClick={addLine} disabled={!modal.supplier_id} style={{ padding: '5px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>＋ Add line</button>
              <div style={{ fontSize: 12, color: '#888' }}>Σ line values (invoiced × rate) = <strong style={{ color: '#3B3A8F' }}>₹{sumLines.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>{num(modal.invoice_amount) > 0 && Math.abs(sumLines - num(modal.invoice_amount)) > 0.5 && <span style={{ color: '#B45309' }}> · differs from typed invoice amount</span>}</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button disabled={busy === 'rec'} onClick={submit} style={{ padding: '8px 18px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{busy === 'rec' ? 'Booking…' : 'Book Receipt'}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {editGrn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setEditGrn(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 22, width: 440, maxWidth: '92vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>Edit GRN {editGrn.grn_no}</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>PO {editGrn.po_no} · corrects a mis-keyed receipt in place</div>
            {editGrn.err && <div style={{ background: '#FDECEC', color: '#A32D2D', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{editGrn.err}</div>}
            {[
              ['received_qty', 'Received qty', 'number'],
              ['rejected_qty', 'Rejected qty', 'number'],
              ['challan_no', 'Challan / DC no', 'text'],
              ['challan_date', 'Challan / DC date', 'date'],
              ['received_date', 'Received date', 'date'],
              ['remarks', 'Remarks', 'text'],
            ].map(([k, label, type]) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 3 }}>{label}</label>
                <input type={type} value={editGrn[k]} onChange={ev => setEditGrn(m => ({ ...m, [k]: ev.target.value }))} style={inp} />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#666', display: 'block', marginBottom: 3 }}>Reason <span style={{ color: '#E22D2D' }}>*</span> (required)</label>
              <textarea value={editGrn.reason} onChange={ev => setEditGrn(m => ({ ...m, reason: ev.target.value }))} rows={2} style={{ ...inp, resize: 'vertical' }} placeholder="Why is this correction being made?" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setEditGrn(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button disabled={busy === 'edit' || !editGrn.reason.trim()} onClick={submitEdit} style={{ padding: '8px 18px', background: editGrn.reason.trim() ? '#3B3A8F' : '#b7b9d0', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: editGrn.reason.trim() ? 'pointer' : 'not-allowed', fontWeight: 600 }}>{busy === 'edit' ? 'Saving…' : 'Save correction'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Item Master (Phase 1) — store-maintained general-store catalogue ─────────
const UOMS = ['pcs', 'kg', 'g', 'm', 'ltr', 'ml', 'box', 'pkt', 'roll', 'set'];
const blankItem = { code: '', name: '', uom: 'pcs', category: '', reorder_level: '', specification: '', hsn_code: '', default_gst_pct: '' };

function ItemMaster() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(blankItem);
  const [editing, setEditing] = React.useState(null);   // id being edited, or null
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => { api.get('/procurement/store-items').then(r => setItems(r.data.data || [])).catch(() => {}); }, []);
  React.useEffect(() => { load(); }, [load]);

  const reset = () => { setForm(blankItem); setEditing(null); setErr(''); };
  const startEdit = (it) => { setEditing(it.id); setErr(''); setForm({ code: it.code, name: it.name, uom: it.uom, category: it.category || '', reorder_level: it.reorder_level ?? '', specification: it.specification || '', hsn_code: it.hsn_code || '', default_gst_pct: it.default_gst_pct != null ? String(Number(it.default_gst_pct)) : '' }); };

  const save = async () => {
    if (!form.code.trim()) { setErr('Code is required'); return; }
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    const body = { name: form.name, uom: form.uom, category: form.category, reorder_level: num(form.reorder_level), specification: form.specification, hsn_code: form.hsn_code || null, default_gst_pct: form.default_gst_pct === '' ? null : Number(form.default_gst_pct) };
    try {
      if (editing) await api.patch(`/procurement/store-items/${editing}`, body);
      else await api.post('/procurement/store-items', { ...body, code: form.code });
      reset(); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const toggleActive = async (it) => {
    try { await api.patch(`/procurement/store-items/${it.id}`, { is_active: !it.is_active }); load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };

  return (
    <>
      <div style={{ marginBottom: 18, padding: '9px 13px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
        The <strong>general-store catalogue</strong> — consumables & supplies the store maintains. Add items here, then raise indents against them.
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>{editing ? 'Edit Item' : 'Add Item'}</div>
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: '1 1 130px' }}><label style={lbl}>Code *</label>
            <input value={form.code} disabled={!!editing} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} style={{ ...inp, background: editing ? '#f5f5f5' : '#fff' }} placeholder="e.g. STAT-001" /></div>
          <div style={{ flex: '2 1 200px' }}><label style={lbl}>Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="e.g. A4 Copier Paper 75gsm" /></div>
          <div style={{ flex: '0 1 100px' }}><label style={lbl}>UOM</label>
            <select value={form.uom} onChange={e => setForm(f => ({ ...f, uom: e.target.value }))} style={inp}>{UOMS.map(u => <option key={u} value={u}>{u}</option>)}</select></div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: '1 1 150px' }}><label style={lbl}>Category</label>
            <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inp} placeholder="e.g. Stationery" /></div>
          <div style={{ flex: '0 1 120px' }}><label style={lbl}>Reorder level</label>
            <input type="number" step="0.001" value={form.reorder_level} onChange={e => setForm(f => ({ ...f, reorder_level: e.target.value }))} style={inp} /></div>
          <div style={{ flex: '2 1 200px' }}><label style={lbl}>Specification</label>
            <input value={form.specification} onChange={e => setForm(f => ({ ...f, specification: e.target.value }))} style={inp} placeholder="optional notes" /></div>
          <div style={{ flex: '0 1 110px' }}><label style={lbl}>HSN Code</label>
            <input value={form.hsn_code || ''} onChange={e => setForm(f => ({ ...f, hsn_code: e.target.value }))} style={inp} maxLength={8} placeholder="4/6/8 digits" /></div>
          <div style={{ flex: '0 1 100px' }}><label style={lbl}>Default GST %</label>
            <select value={form.default_gst_pct ?? ''} onChange={e => setForm(f => ({ ...f, default_gst_pct: e.target.value }))} style={inp}><option value="">—</option>{['0', '0.25', '3', '5', '12', '18', '28'].map(s => <option key={s} value={s}>{s}%</option>)}</select></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {editing && <button onClick={reset} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>}
          <button disabled={busy} onClick={save} style={{ padding: '8px 18px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>{busy ? 'Saving…' : editing ? 'Save Changes' : '＋ Add Item'}</button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Catalogue ({items.length})</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Code', 'Name', 'UOM', 'Category', 'Reorder', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa' }}>No items yet — add the first above.</td></tr>}
            {items.map(it => (
              <tr key={it.id} style={{ opacity: it.is_active ? 1 : 0.5 }}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{it.code}</td>
                <td style={td}>{it.name}</td>
                <td style={td}>{it.uom}</td>
                <td style={td}>{it.category || '—'}</td>
                <td style={td}>{num(it.reorder_level).toLocaleString('en-IN')}</td>
                <td style={td}>{it.is_active ? <span style={{ color: '#166534', fontSize: 12, fontWeight: 600 }}>active</span> : <span style={{ color: '#999', fontSize: 12 }}>inactive</span>}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => startEdit(it)} style={{ padding: '5px 10px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Edit</button>
                  <button onClick={() => toggleActive(it)} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: it.is_active ? '#A32D2D' : '#166534' }}>{it.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Indents (Phase 1) — the store raises a general-store requisition ─────────
const STATUS_COLORS = { draft: '#888', pending: '#B45309', approved: '#166534', rejected: '#A32D2D', cancelled: '#888' };

function Indents({ user }) {
  // WHO SEES WHICH INDENTS. The list used to be loaded with mine=1 unconditionally, which the
  // backend resolves to raised_by = req.user.id. Every indent on the system is raised by the
  // store login, so admin / management / stores_incharge saw an EMPTY table and never got the
  // "Place PO →" button — 19 approved indents sat untouched because the only people authorised
  // to act on them could not see them.
  //
  // Scope now follows INDENT_PO_AUTH (procurement.routes.js:879 = store, stores_incharge,
  // admin, management): a `store` user still sees only what they raised; everyone else
  // authorised to place a PO sees ALL indents, which is the whole point of their being
  // authorised. Anyone else who reaches this tab falls back to their own (safe default).
  const seesAll = ['admin', 'management', 'stores_incharge'].includes(user?.role);
  const [items, setItems] = React.useState([]);
  const [indents, setIndents] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [lines, setLines] = React.useState([{ stock_item_id: '', qty: '', rate: '', notes: '' }]);
  const [purpose, setPurpose] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [po, setPo] = React.useState(null);   // { indent, lines, supplier_id, expected_date, notes, rates } or null
  const [poErr, setPoErr] = React.useState('');
  // Kept separate from `err` (the raise-form banner) so a list-load failure can't surface as a
  // confusing message under the "Raise Indent" form. It used to be swallowed by .catch(() => {}).
  const [listErr, setListErr] = React.useState('');
  const loadItems = React.useCallback(() => { api.get('/procurement/store-items?active=1').then(r => setItems(r.data.data || [])).catch(() => {}); }, []);
  const loadIndents = React.useCallback(() => {
    api.get('/requests?type=indent_general' + (seesAll ? '' : '&mine=1'))
      .then(r => setIndents(r.data.data || []))
      .catch(e => setListErr(e.response?.data?.error || 'Could not load indents'));
  }, [seesAll]);
  React.useEffect(() => {
    loadItems(); loadIndents();
    api.get('/suppliers').then(r => setSuppliers((r.data.data || []).filter(s => s.is_active !== false))).catch(() => {});
  }, [loadItems, loadIndents]);

  const openPlacePO = async (indent) => {
    setPoErr('');
    try {
      const r = await api.get(`/requests/${indent.id}`);
      const ln = r.data.data.lines || [];
      // A line already carrying a live GSPO is not offered. place-po refuses it anyway, and
      // it used to refuse AFTER a vendor and a rate had been typed — the last step rather
      // than the first. placed_po_no comes off the same helper the guard reads.
      setPo({ indent, lines: ln, supplier_id: '', expected_date: '', notes: '',
        // Everything free starts ticked: placing the whole indent stays one click, and the
        // ticks only matter when part of it is already ordered.
        pick: Object.fromEntries(ln.filter(l => !l.placed_po_no).map(l => [l.id, true])),
        vendors: {},
        rates: Object.fromEntries(ln.map(l => [l.id, l.rate || ''])),
        // The store states the quantity it orders (mig 399). Starts at the indent's figure; a different
        // figure needs a reason, recorded against the indent's.
        qtys: Object.fromEntries(ln.map(l => [l.id, l.qty != null ? String(num(l.qty)) : ''])),
        qtyReasons: {} });
    } catch (e) { alert(e.response?.data?.error || 'Could not load indent'); }
  };
  const placePO = async () => {
    // Only the ticked, unplaced lines are being ordered — every check below is about THEM.
    // Validating the whole indent is what made a re-order of one cancelled line impossible:
    // the four still on order have no rate to give and would fail a blanket check.
    const going = po.lines.filter(l => !l.placed_po_no && po.pick[l.id]);
    if (!going.length) { setPoErr('Tick at least one line to order.'); return; }
    if (!po.expected_date) { setPoErr('Set an expected date'); return; }
    // ONE VENDOR PER LINE: a line's own vendor if set, otherwise the header one. The header
    // is only required when some ticked line is relying on it.
    const vendorFor = (l) => po.vendors[l.id] || po.supplier_id;
    const noVendor = going.filter(l => !vendorFor(l));
    if (noVendor.length) { setPoErr(`Pick a supplier — ${noVendor.length === going.length ? 'no line has one' : `${noVendor.map(l => l.item_desc).join(', ')} ${noVendor.length === 1 ? 'has' : 'have'} none`}.`); return; }
    const changed = (l) => Math.abs(num(po.qtys[l.id]) - num(l.qty)) > 0.0005;
    const noQty = going.filter(l => !(num(po.qtys[l.id]) > 0));
    if (noQty.length) { setPoErr(`Needs a quantity greater than 0: ${noQty.map(l => l.item_desc).join(', ')}`); return; }
    const noWhy = going.filter(l => changed(l) && String(po.qtyReasons[l.id] || '').trim().length < 5);
    if (noWhy.length) { setPoErr(`Say why the quantity differs from the indent: ${noWhy.map(l => l.item_desc).join(', ')}`); return; }
    const unpriced = going.filter(l => !(num(po.rates[l.id]) > 0));
    if (unpriced.length) { setPoErr(`Needs a unit price greater than 0: ${unpriced.map(l => l.item_desc).join(', ')}`); return; }
    setBusy(true); setPoErr('');
    try {
      await api.post(`/procurement/indent/${po.indent.id}/place-po`, {
        // supplier_id stays as the fallback the API expects for any line without its own.
        supplier_id: po.supplier_id || vendorFor(going[0]), expected_date: po.expected_date, notes: po.notes,
        lines: going.map(l => ({ request_line_id: l.id, supplier_id: vendorFor(l), rate: num(po.rates[l.id]),
          ...(changed(l) ? { qty: num(po.qtys[l.id]), qty_reason: String(po.qtyReasons[l.id] || '').trim() } : {}) })),
      });
      setPo(null); loadIndents();
    } catch (e) { setPoErr(e.response?.data?.error || 'Failed to place PO'); } finally { setBusy(false); }
  };

  const setLine = (i, patch) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, { stock_item_id: '', qty: '', rate: '', notes: '' }]);
  const rmLine = (i) => setLines(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls);
  const uomOf = (id) => (items.find(x => x.id === id) || {}).uom || '';

  const raise = async () => {
    const clean = lines.filter(l => l.stock_item_id && num(l.qty) > 0);
    if (!clean.length) { setErr('Add at least one item with a quantity'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/requests', {
        type: 'indent_general', purpose, submit: true,
        lines: clean.map(l => ({ stock_item_id: l.stock_item_id, qty: num(l.qty), rate: num(l.rate), notes: l.notes })),
      });
      setLines([{ stock_item_id: '', qty: '', rate: '', notes: '' }]); setPurpose(''); loadIndents();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to raise indent'); } finally { setBusy(false); }
  };

  // AMEND — reuses the raise form rather than building a second one, so the two can never
  // drift into different validation. `amending` holds the indent being corrected; while it
  // is set the form saves through PATCH /amend instead of POST.
  //
  // Raised-only by design: an approved figure must not move (the server 409s on approved).
  // A pending indent comes back to draft on save, so the approver re-reads it — the response
  // says so and the message repeats it, because a silent status change is worse than none.
  const [amending, setAmending] = React.useState(null);
  const [amendReason, setAmendReason] = React.useState('');
  const openAmend = async (it) => {
    setErr('');
    try {
      const r = await api.get(`/requests/${it.id}`);
      const d = r.data.data || {};
      setAmending({ id: it.id, request_no: it.request_no, status: it.status });
      setAmendReason('');
      setPurpose(d.purpose || '');
      setLines((d.lines || []).length
        ? d.lines.map(l => ({ stock_item_id: l.stock_item_id || '', qty: String(l.qty ?? ''), rate: String(l.rate ?? ''), notes: l.notes || '' }))
        : [{ stock_item_id: '', qty: '', rate: '', notes: '' }]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setErr(e.response?.data?.error || 'Could not load that indent'); }
  };
  const cancelAmend = () => {
    setAmending(null); setAmendReason(''); setPurpose('');
    setLines([{ stock_item_id: '', qty: '', rate: '', notes: '' }]);
  };
  const saveAmend = async () => {
    const clean = lines.filter(l => l.stock_item_id && num(l.qty) > 0);
    if (!clean.length) { setErr('Add at least one item with a quantity'); return; }
    if (!amendReason.trim()) { setErr('A reason is required to amend an indent'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.patch(`/requests/${amending.id}/amend`, {
        purpose, reason: amendReason.trim(),
        lines: clean.map(l => ({ stock_item_id: l.stock_item_id, item_desc: (items.find(x => x.id === l.stock_item_id) || {}).name || '',
          qty: num(l.qty), unit: uomOf(l.stock_item_id), rate: num(l.rate), notes: l.notes })),
      });
      const d = r.data || {};
      cancelAmend(); loadIndents();
      setErr(d.returned_to_draft
        ? `${amending.request_no} amended — total ₹${num(d.total).toLocaleString('en-IN')}. It was pending, so it is back in DRAFT and must be submitted again.`
        : `${amending.request_no} amended — total ₹${num(d.total).toLocaleString('en-IN')}.`);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to amend'); } finally { setBusy(false); }
  };
  // IN-ROW CONFIRMATION, NOT window.confirm. A browser dialog is suppressible — Chrome's
  // "prevent additional dialogs" sticks for the life of the tab — and a suppressed confirm
  // returns false, so the handler returned before the request with no error shown: a click
  // that silently did nothing. Same treatment as the Approvals hub.
  //
  // Cancelling an APPROVED indent additionally needs a reason (the server refuses without
  // one), so the armed row shows an input rather than a plain yes/no.
  const [cancelling, setCancelling] = React.useState(null);   // { id, request_no, needsReason }
  const [cancelReason, setCancelReason] = React.useState('');
  const closeCancel = () => { setCancelling(null); setCancelReason(''); };
  const cancelIndent = async (it, reason) => {
    try {
      await api.patch(`/requests/${it.id}/cancel`, reason ? { reason } : {});
      closeCancel(); loadIndents();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to cancel'); }
  };
  // Open the letterhead PDF. Fetched as a blob (so the Bearer token rides along) then
  // opened in a new tab for print/save — the PDF route is Bearer-auth, not a public link.
  const printIndent = async (it) => {
    try {
      const r = await api.get(`/requests/${it.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert(e.response?.data?.error || 'Could not generate the indent PDF'); }
  };

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  const inp = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };

  // ── THE CARTON QUEUE (mig 277) ────────────────────────────────────────────────────────────
  // An auto-raised indent nobody is told about is the failure this path exists to avoid: 53 of
  // 57 approved indents have never had a PO placed, and 8 GSPOs sit unreceived. So the two
  // states that need a human are surfaced ABOVE the form rather than left to be found in the
  // table below it.
  //
  // "Approved, PO not placed" is the actionable one — it is the exact bucket those 53 fell into.
  // "Awaiting approval" is informational: the store cannot act (and by the decision route's
  // raiser-never-decides rule, cannot approve its own indent), but it should know what is coming.
  // Carton indents are AUTO-APPROVED at ARS release (the decision was taken there; a second
  // approval is theatre), so they arrive ready to order and there is no "awaiting approval"
  // bucket to show. `cartonPending` is kept only to surface any legacy row raised before that
  // ruling — it renders nothing once there are none.
  // Reject an auto-created carton indent. A reason is required — the row will read as a rejection
  // forever, and "why" is the only thing that distinguishes a correction from a refusal.
  const rejectCarton = async (i) => {
    const remarks = window.prompt(
      `Reject carton indent ${i.request_no}?\n\nThe cartons return to the ARS as unordered and merchandising can release them again.\n\nReason:`);
    if (remarks == null) return;
    if (!remarks.trim()) { setListErr('A reason is required to reject a carton indent.'); return; }
    try {
      const r = await api.patch(`/requests/${i.id}/decision`, { decision: 'rejected', remarks: remarks.trim() });
      setListErr('');
      loadIndents();
      const n = r.data?.ars_lines_released || 0;
      window.alert(`${i.request_no} rejected.${n ? ` ${n} ARS carton line(s) returned to unordered.` : ''}`);
    } catch (e) {
      setListErr(e.response?.data?.error || 'Could not reject this indent');
    }
  };

  const cartons = indents.filter(i => i.origin === 'carton_release');
  const cartonToPlace = cartons.filter(i => i.status === 'approved' && !i.po_no);
  const cartonPending = cartons.filter(i => i.status === 'pending');

  return (
    <>
      {(cartonToPlace.length > 0 || cartonPending.length > 0) && (
        <div style={{ marginBottom: 18, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E3F5' }}>
          <div style={{ padding: '9px 13px', background: '#3B3A8F', color: '#fff', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
            Cartons released to the store
          </div>
          <div style={{ background: '#fff', padding: 14 }}>
            {cartonToPlace.length > 0 && (
              <div style={{ marginBottom: cartonPending.length ? 12 : 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#854F0B', marginBottom: 7 }}>
                  {cartonToPlace.length} ready to order — no PO placed yet
                </div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  Released by merchandising and approved automatically. Nobody else is going to act on these.
                </div>
                {cartonToPlace.map(i => (
                  <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#FEF3C7', borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                    <strong>{i.request_no}</strong>
                    <span style={{ color: '#666' }}>{i.purpose}</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      {/* THE WAY OUT. A carton indent is created and approved by the release —
                          nobody chose it — so the store must be able to say it is wrong. Only
                          reachable while nothing has been ordered; the backend refuses after that
                          and names the PO. */}
                      <button onClick={() => rejectCarton(i)} style={{ padding: '5px 12px', background: '#fff', color: '#B91C1C', border: '1px solid #E5B4B4', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Reject
                      </button>
                      <button onClick={() => openPlacePO(i)} style={{ padding: '5px 12px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Place PO →
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {cartonPending.length > 0 && (
              <div style={{ fontSize: 13, color: '#666' }}>
                {cartonPending.length} still awaiting approval (raised before auto-approval):{' '}
                {cartonPending.map(i => i.request_no).join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 18, padding: '9px 13px', background: '#EEEDF8', borderRadius: 6, fontSize: 13, color: '#3B3A8F' }}>
        Raise a <strong>general-store indent</strong> for consumables from the catalogue. It goes to <strong>management</strong> for approval; once approved, the store places the PO.
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: amending ? '#854F0B' : '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>
          {amending ? `Amending ${amending.request_no}` : 'New Indent'}
        </div>
        {/* The form is the SAME one used to raise, so it has to say which act it is doing —
            and warn about the status change before it happens, not in the result banner. */}
        {amending && (
          <div style={{ background: '#FEF3C7', color: '#854F0B', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            Correcting <strong>{amending.request_no}</strong> ({amending.status}).
            {amending.status === 'pending' && ' Saving returns it to DRAFT — it must be submitted again so the approver reads the corrected figure.'}
          </div>
        )}
        {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
          <thead><tr>{['Item', 'Qty', 'UOM', 'Rate (opt.)', 'Notes', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={{ ...td, minWidth: 220 }}>
                  <select value={l.stock_item_id} onChange={e => setLine(i, { stock_item_id: e.target.value })} style={inp}>
                    <option value="">— pick an item —</option>
                    {items.map(it => <option key={it.id} value={it.id}>{it.code} · {it.name}</option>)}
                  </select>
                </td>
                <td style={{ ...td, width: 90 }}><input type="number" step="0.001" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} style={inp} /></td>
                <td style={{ ...td, width: 50, color: '#888' }}>{uomOf(l.stock_item_id)}</td>
                <td style={{ ...td, width: 100 }}><input type="number" step="0.01" value={l.rate} onChange={e => setLine(i, { rate: e.target.value })} style={inp} /></td>
                <td style={td}><input value={l.notes} onChange={e => setLine(i, { notes: e.target.value })} style={inp} /></td>
                <td style={{ ...td, width: 30 }}>{lines.length > 1 && <button onClick={() => rmLine(i)} style={{ background: 'transparent', border: 'none', color: '#A32D2D', cursor: 'pointer', fontSize: 16 }}>×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addLine} style={{ padding: '5px 12px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginBottom: 12 }}>＋ Add line</button>
        <div style={{ marginBottom: 14 }}><label style={lbl}>Purpose / remarks</label><input value={purpose} onChange={e => setPurpose(e.target.value)} style={{ ...inp, padding: '8px 10px' }} placeholder="e.g. Monthly office stationery" /></div>
        {amending && (
          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Reason for the amendment *</label>
            <input value={amendReason} onChange={e => setAmendReason(e.target.value)}
              style={{ ...inp, padding: '8px 10px', borderColor: amendReason.trim() ? undefined : '#E0B4B4' }}
              placeholder="e.g. rate corrected against the supplier quote" />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {amending && <button onClick={cancelAmend} style={{ padding: '9px 16px', background: 'transparent', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#666' }}>Discard changes</button>}
          <button disabled={busy || (amending && !amendReason.trim())}
            onClick={amending ? saveAmend : raise}
            style={{ padding: '9px 20px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600,
              opacity: (amending && !amendReason.trim()) ? 0.5 : 1 }}>
            {busy ? (amending ? 'Saving…' : 'Raising…') : amending ? `Save amendment to ${amending.request_no}` : 'Raise Indent →'}
          </button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          {seesAll ? 'All Indents' : 'My Indents'} ({indents.length})
        </div>
        {listErr && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{listErr}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          {/* "Raised by" only when the list spans more than one raiser — without it an
              all-indents view gives no way to tell whose indent is whose. */}
          <thead><tr>{['Indent No', 'Raised', ...(seesAll ? ['Raised by'] : []), 'Amount', 'Status', 'PO', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {indents.length === 0 && <tr><td colSpan={seesAll ? 7 : 6} style={{ ...td, textAlign: 'center', color: '#aaa' }}>No indents yet.</td></tr>}
            {indents.map(it => (
              <tr key={it.id}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{it.request_no}</td>
                <td style={td}>{(it.raised_at || '').slice(0, 10)}</td>
                {seesAll && <td style={td}>{it.raised_by_name || '—'}</td>}
                <td style={td}>{it.amount != null ? '₹' + num(it.amount).toLocaleString('en-IN') : '—'}</td>
                <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: STATUS_COLORS[it.status] || '#555', background: '#f4f4f8' }}>{it.status}</span></td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#888' }}>{it.po_no || '—'}</td>
                <td style={td}>
                  {cancelling && cancelling.id === it.id ? (
                    /* ARMED — the row says what it is about to do, and asks for the reason
                       when the server will require one (approved only). Escape backs out. */
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}
                      onKeyDown={e => { if (e.key === 'Escape') closeCancel(); }}>
                      {cancelling.needsReason ? (
                        <input autoFocus value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && cancelReason.trim()) cancelIndent(it, cancelReason.trim()); }}
                          placeholder="Reason (required)"
                          style={{ padding: '5px 8px', border: '1px solid #E0B4B4', borderRadius: 5, fontSize: 12, width: 190 }} />
                      ) : (
                        <span style={{ fontSize: 11, color: '#555' }}>Cancel {it.request_no}?</span>
                      )}
                      <button autoFocus={!cancelling.needsReason}
                        disabled={cancelling.needsReason && !cancelReason.trim()}
                        onClick={() => cancelIndent(it, cancelReason.trim())}
                        style={{ padding: '5px 11px', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700,
                          cursor: 'pointer', background: '#A32D2D', color: '#fff',
                          opacity: (cancelling.needsReason && !cancelReason.trim()) ? 0.45 : 1 }}>✕ Yes, cancel</button>
                      <button onClick={closeCancel} style={{ padding: '5px 9px', background: 'transparent', border: '1px solid #ddd', borderRadius: 5, color: '#666', fontSize: 12, cursor: 'pointer' }}>Keep</button>
                    </div>
                  ) : (<>
                  {['draft', 'pending'].includes(it.status) && <button onClick={() => { setCancelReason(''); setCancelling({ id: it.id, needsReason: false }); }} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#A32D2D' }}>Cancel</button>}
                  {/* AMEND is raised-only, by design: an approved figure must not move. The
                      server 409s on approved, so this is the same rule stated twice. */}
                  {['draft', 'pending'].includes(it.status) && <button onClick={() => openAmend(it)} style={{ padding: '5px 10px', background: '#EEEDF8', border: '1px solid #cfcbe8', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#3B3A8F', fontWeight: 600, marginLeft: 6 }}>✎ Amend</button>}
                  {/* Cancelling an APPROVED indent reverses somebody else's decision, so it is
                      admin/management only — the same gate the server enforces. */}
                  {it.status === 'approved' && ['admin', 'management'].includes(user?.role) && <button onClick={() => { setCancelReason(''); setCancelling({ id: it.id, needsReason: true }); }} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #E0B4B4', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#A32D2D', marginRight: 6 }}>Cancel</button>}
                  {it.status === 'approved' && !it.po_no && <button onClick={() => openPlacePO(it)} style={{ padding: '5px 12px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Place PO →</button>}
                  {it.status === 'approved' && <button onClick={() => printIndent(it)} style={{ padding: '5px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px solid #cfcbe8', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, marginLeft: 6 }}>🖨 Print</button>}
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {po && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 4px' }}>Place PO — {po.indent.request_no}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#888' }}>
              General-store PO — one PO per line, so lines can go to different vendors and be re-ordered independently.
              {po.lines.some(l => l.placed_po_no) && ' Lines already on order are shown but cannot be picked; cancel their PO to order them again.'}
            </p>
            {poErr && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{poErr}</div>}
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 2 }}><label style={lbl}>Supplier *</label>
                <select value={po.supplier_id} onChange={e => setPo(p => ({ ...p, supplier_id: e.target.value }))} style={inp}>
                  <option value="">— pick a supplier —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div style={{ fontSize: 10.5, color: '#888', marginTop: 3 }}>Used for any line without its own vendor below.</div></div>
              <div style={{ flex: 1 }}><label style={lbl}>Expected date *</label>
                <input type="date" value={po.expected_date} onChange={e => setPo(p => ({ ...p, expected_date: e.target.value }))} style={inp} /></div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
              <thead><tr>{['', 'Item', 'Qty', 'Vendor', 'Unit price *'].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {po.lines.map(l => {
                  const placed = !!l.placed_po_no;
                  return (
                  <tr key={l.id} style={{ opacity: placed ? 0.55 : 1 }}>
                    <td style={{ ...td, width: 26 }}>
                      {/* A placed line is not a choice, so it gets no control to click —
                          the PO number below says what it is instead. */}
                      {!placed && <input type="checkbox" checked={!!po.pick[l.id]}
                        onChange={e => setPo(p => ({ ...p, pick: { ...p.pick, [l.id]: e.target.checked } }))} />}
                    </td>
                    <td style={td}>
                      {l.item_desc}
                      {placed && <div style={{ fontSize: 10.5, color: '#B45309' }}>on order · {l.placed_po_no} · {l.placed_status}</div>}
                    </td>
                    <td style={{ ...td, width: 150 }}>
                      {placed ? <span>{num(l.qty).toLocaleString('en-IN')} {l.unit}</span> : (<>
                        <input type="number" step="1" min="0" disabled={!po.pick[l.id]} value={po.qtys[l.id]}
                          onChange={e => setPo(p => ({ ...p, qtys: { ...p.qtys, [l.id]: e.target.value } }))} style={inp} />
                        <div style={{ fontSize: 10.5, color: '#888' }}>indent: {num(l.qty).toLocaleString('en-IN')} {l.unit}</div>
                        {Math.abs(num(po.qtys[l.id]) - num(l.qty)) > 0.0005 && (
                          <input placeholder="Why this quantity? (recorded)" value={po.qtyReasons[l.id] || ''}
                            onChange={e => setPo(p => ({ ...p, qtyReasons: { ...p.qtyReasons, [l.id]: e.target.value } }))}
                            style={{ ...inp, marginTop: 4 }} />)}
                      </>)}
                    </td>
                    <td style={{ ...td, width: 150 }}>
                      {placed ? <span style={{ fontSize: 11, color: '#888' }}>—</span> : (
                        <select value={po.vendors[l.id] || ''} disabled={!po.pick[l.id]}
                          onChange={e => setPo(p => ({ ...p, vendors: { ...p.vendors, [l.id]: e.target.value } }))} style={inp}>
                          <option value="">— same as above —</option>
                          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>)}
                    </td>
                    <td style={{ ...td, width: 120 }}>
                      {placed ? <span style={{ fontSize: 11, color: '#888' }}>—</span> : (
                        <input type="number" step="0.01" disabled={!po.pick[l.id]} value={po.rates[l.id]}
                          onChange={e => setPo(p => ({ ...p, rates: { ...p.rates, [l.id]: e.target.value } }))} style={inp} />)}
                    </td>
                  </tr>);
                })}
              </tbody>
            </table>
            <div style={{ marginBottom: 16 }}><label style={lbl}>Notes</label>
              <input value={po.notes} onChange={e => setPo(p => ({ ...p, notes: e.target.value }))} style={{ ...inp, padding: '8px 10px' }} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setPo(null)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button disabled={busy} onClick={placePO} style={{ padding: '8px 18px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                {busy ? 'Placing…' : (() => { const n = po.lines.filter(l => !l.placed_po_no && po.pick[l.id]).length;
                  return n === po.lines.length ? 'Place PO' : `Place ${n} PO${n === 1 ? '' : 's'}`; })()}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── My Bills — the store user's OWN receipt invoices (read-only) ─────────────
const INV_ST = { pending: { c: '#B45309', bg: '#FEF3E2', l: 'pending review' }, approved: { c: '#166534', bg: '#EAF7EE', l: 'approved' }, rejected: { c: '#A32D2D', bg: '#FCEBEB', l: 'rejected' } };
function MyBills() {
  const [rows, setRows] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [stFilter, setStFilter] = React.useState('');
  React.useEffect(() => { api.get('/procurement/my-invoices').then(r => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoaded(true)); }, []);
  // CLIENT-SIDE, like the POs tab below. 246 rows for the busiest user and the whole set is
  // already on the wire, so filtering here is instant and needs no endpoint change.
  //
  // FOUR FIELDS, MATCHING WHAT THE ROW SHOWS. Searching by something invisible is how a person
  // concludes the box is broken. po_nos is searched ACROSS THE ARRAY: 33 bills span more than
  // one PO and pi.po_no is only the first, so matching that alone would miss the rest.
  //
  // AMOUNT IS DELIBERATELY NOT SEARCHABLE. Substring matching on money misleads — "296" would
  // hit Rs 296, Rs 2,960 and Rs 1,296 alike. If amount filtering is wanted it is a min/max
  // pair, not a text box.
  const shown = rows.filter(b => {
    if (stFilter && (b.review_status || 'pending') !== stFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const pos = (b.po_nos && b.po_nos.length ? b.po_nos : (b.po_no ? [b.po_no] : []));
    return [b.invoice_no, b.supplier, b.grn_nos, ...pos].some(v => String(v || '').toLowerCase().includes(s));
  });
  // Counted off the UNFILTERED set, so a chip says how many EXIST rather than how many survive
  // the current search — a count that moves as you type is not a count.
  const countOf = (k) => rows.filter(b => (b.review_status || 'pending') === k).length;
  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>
          My Bills ({shown.length}{shown.length !== rows.length ? ' of ' + rows.length : ''})</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search invoice / vendor / PO / GRN"
          style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 280 }} />
      </div>
      <div style={{ fontSize: 12, color: '#888', margin: '6px 0 10px' }}>Invoices you booked at goods receipt. Finance reviews each before it becomes payable.</div>
      {/* STATUS AS CHIPS, NOT A SEARCH TERM. Three values, and "show me my pending ones" is the
          real question from someone holding 111 of them. Typed, the word would also match a
          rejection reason that happens to contain it. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['', 'All', rows.length], ['pending', INV_ST.pending.l, countOf('pending')],
          ['approved', INV_ST.approved.l, countOf('approved')], ['rejected', INV_ST.rejected.l, countOf('rejected')]]
          .filter(function (c) { return c[0] === '' || c[2] > 0; })   // a chip for a status you have none of is noise
          .map(function (c) { return (
            <button key={c[0] || 'all'} onClick={() => setStFilter(c[0])} style={{
              padding: '4px 12px', borderRadius: 14, fontSize: 12, cursor: 'pointer', fontWeight: 600,
              border: '1px solid ' + (stFilter === c[0] ? '#3B3A8F' : '#ddd'),
              background: stFilter === c[0] ? '#EEEDF8' : '#fff', color: stFilter === c[0] ? '#3B3A8F' : '#777' }}>
              {c[1]} <span style={{ color: '#aaa', fontWeight: 400 }}>{c[2]}</span>
            </button>); })}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Invoice No', 'Vendor', 'Date', 'PO', 'GRN', 'Amount', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {loaded && shown.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#aaa' }}>
            {rows.length ? 'No bills match.' : 'No bills yet — book a receipt in the Inward tab.'}</td></tr>}
          {shown.map(b => { const st = INV_ST[b.review_status] || INV_ST.pending; return (
            <tr key={b.id}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{b.invoice_no}</td>
              <td style={td}>{b.supplier || '—'}</td>
              <td style={td}>{(b.invoice_date || '').slice(0, 10)}</td>
              {/* A multi-item receipt covers several POs; po_no is only the first of them. */}
              <td style={{ ...td, fontFamily: 'monospace', color: '#888', fontSize: 11, lineHeight: 1.5 }}>
                {(() => { const l = (b.po_nos && b.po_nos.length ? b.po_nos : (b.po_no ? [b.po_no] : []));
                  return l.length ? l.map((p, i) => <div key={p + i}>{p}</div>) : '—'; })()}
              </td>
              <td style={{ ...td, fontFamily: 'monospace', color: b.grn_nos ? '#166534' : '#ccc' }}>{b.grn_nos || '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>₹{num(b.total_amount).toLocaleString('en-IN')}</td>
              <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: st.c, background: st.bg }}>{st.l}</span>
                {b.review_status === 'rejected' && b.rejection_reason && <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3, maxWidth: 220 }}>✕ {b.rejection_reason}</div>}</td>
            </tr>
          ); })}
        </tbody>
      </table>
    </div>
  );
}

// ─── POs — read-only PO list (awaiting receipt + received last 30 days) ────────
const PO_ST = { approved: { c: '#B45309', bg: '#FEF3E2', l: 'awaiting receipt' }, received: { c: '#166534', bg: '#EAF7EE', l: 'received' } };
function PurchaseOrders() {
  const [rows, setRows] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [q, setQ] = React.useState('');
  React.useEffect(() => { api.get('/procurement/pos-for-store').then(r => setRows(r.data.data || [])).catch(() => {}).finally(() => setLoaded(true)); }, []);
  const itemsOf = (p) => { let j = []; try { j = typeof p.items_json === 'string' ? JSON.parse(p.items_json || '[]') : (p.items_json || []); } catch { j = []; } return j.length ? j.map(x => x.item_desc || x.description || x.name).filter(Boolean).join(', ') : (p.description || p.material_type || '—'); };
  const shown = rows.filter(p => { const s = q.trim().toLowerCase(); if (!s) return true; return [p.po_no, p.gpo_no, p.supplier_name].some(v => String(v || '').toLowerCase().includes(s)); });
  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const card = { background: '#fff', borderRadius: 10, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1 }}>Purchase Orders ({shown.length}) <span style={{ color: '#aaa', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· awaiting receipt + received in last 30 days · read-only</span></div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search PO / GPO / vendor" style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 240 }} />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['PO No', 'Vendor', 'Items', 'Qty', 'Expected', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {loaded && shown.length === 0 && <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{rows.length ? 'No matches.' : 'No POs awaiting receipt or received recently.'}</td></tr>}
          {shown.map(p => { const st = PO_ST[p.status] || { c: '#888', bg: '#f4f4f8', l: p.status }; return (
            <tr key={p.id}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{p.po_no}{p.gpo_no && <div style={{ fontSize: 10, color: '#E22D2D', fontWeight: 600 }}>{p.gpo_no}</div>}</td>
              <td style={td}>{p.supplier_name || '—'}</td>
              <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={itemsOf(p)}>{itemsOf(p)}</td>
              <td style={td}>{num(p.quantity).toLocaleString('en-IN')} {p.unit}{num(p.received_so_far) > 0 && p.status !== 'received' ? <span style={{ color: '#B45309', fontSize: 11 }}> · {num(p.remaining).toLocaleString('en-IN')} left</span> : null}</td>
              <td style={td}>{(p.expected_date || '').slice(0, 10) || '—'}</td>
              <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 600, color: st.c, background: st.bg }}>{st.l}</span></td>
            </tr>
          ); })}
        </tbody>
      </table>
    </div>
  );
}

// ============================ Internal Store Issue & RCV (mig 150) ============================
const sTh = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const sTd = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
const sInp = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const sBtn = { padding: '7px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const SI_STATUS_COLORS = { draft: '#888', issued: '#3B3A8F', acknowledged: '#166534', partially_returned: '#B45309', closed: '#555', cancelled: '#B91C1C' };
const statusPill = (s) => <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: SI_STATUS_COLORS[s] || '#888' }}>{s}</span>;

function StockOnHand() {
  const { user } = useAuth();
  const canAdjust = ['admin', 'stores_incharge', 'management'].includes(user?.role);
  const [rows, setRows] = React.useState([]);
  const [adj, setAdj] = React.useState(null);
  const [busy, setBusy] = React.useState(false); const [err, setErr] = React.useState('');
  // Receipts that never reached the ledger (mig 223). On-hand is only as true as what posted
  // into it, so this belongs ON this screen — a zero balance beside goods sitting in the
  // building is the thing the store keeper needs told, not left to discover.
  const [unposted, setUnposted] = React.useState(null);
  const load = React.useCallback(() => {
    api.get('/store/stock-on-hand').then(r => setRows(r.data.data || [])).catch(() => {});
    api.get('/store/unposted-receipts').then(r => setUnposted(r.data.data || null)).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const postAdj = async () => {
    setErr('');
    if (!adj.stock_item_id) return setErr('Pick an item.');
    if (!num(adj.qty)) return setErr('Enter a signed quantity (+ count-in / − count-out).');
    if ((adj.reason || '').trim().length < 4) return setErr('A reason is required.');
    setBusy(true);
    try {
      await api.post('/store/stock-adjust', { stock_item_id: adj.stock_item_id, qty: num(adj.qty), reason: adj.reason.trim(), unit_cost: adj.unit_cost !== '' ? num(adj.unit_cost) : undefined });
      setAdj(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Adjust failed'); } finally { setBusy(false); }
  };
  return (
    <div>
      {/* Two lines, deliberately different in tone. The red one is what is genuinely
          unaccounted for. Yarn is NOT in it — it reconciles to the gram in its own chain, and
          showing it as missing made the largest number on this screen a false alarm. It stays
          visible, calmly, because someone who knows ₹1.4m of yarn arrived should be able to
          see where it went rather than watch it disappear off the screen.
          Values are ACCEPTED (received − rejected): goods sent back were never held. */}
      {unposted && unposted.unposted > 0 && (
        <div style={{ padding: '11px 13px', background: '#FCEBEB', border: '1px solid #F0C0C0', borderRadius: 8, marginBottom: 10, fontSize: 12, color: '#A32D2D' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            {unposted.unposted} receipt{unposted.unposted === 1 ? '' : 's'} never reached stock — ₹{Number(unposted.unposted_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })} of goods
          </div>
          <div style={{ marginTop: 3 }}>These arrived and were booked, but no stock movement was recorded. The balances below do not include them.</div>
          <div style={{ marginTop: 7, display: 'grid', gap: 4 }}>
            {[['no_stock_item', 'Recoverable'], ['no_master_match', 'Needs master work'], ['unexplained', 'Unexplained']].map(([k, label]) => {
              const b = unposted.reasons?.[k]; if (!b || !b.receipts) return null;
              return (
                <div key={k} style={{ fontSize: 11 }}>
                  <strong>{label} — {b.receipts} receipt{b.receipts === 1 ? '' : 's'}, ₹{Number(b.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}.</strong> {b.remedy}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {unposted?.tracked_elsewhere?.yarn?.receipts > 0 && (
        <div style={{ padding: '9px 12px', background: '#F4F6FA', border: '1px solid #DDE3EE', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#41506B' }}>
          <strong>Yarn is tracked elsewhere, not here — {unposted.tracked_elsewhere.yarn.receipts} receipts, {Number(unposted.tracked_elsewhere.yarn.qty).toLocaleString('en-IN')} kg, ₹{Number(unposted.tracked_elsewhere.yarn.value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}.</strong>
          <div style={{ marginTop: 3, fontSize: 11 }}>{unposted.tracked_elsewhere.yarn.where} Not missing, and deliberately not counted above.</div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#888' }}>Perpetual on-hand = Σ receipts − Σ issued + Σ returned (the stock ledger). Value at weighted-average cost.</span>
        {canAdjust && <button onClick={() => setAdj({ stock_item_id: '', qty: '', reason: 'Opening physical count', unit_cost: '' })} style={sBtn}>Adjust / Opening Count</button>}
      </div>
      {adj && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 480, padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}><b>Stock Adjustment / Opening Count</b><button onClick={() => setAdj(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button></div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>No approval step — the reason + the ledger row are the audit. A count can't take on-hand negative.</div>
            <label style={{ fontSize: 12, color: '#555' }}>Item<br />
              <select value={adj.stock_item_id} onChange={e => setAdj(a => ({ ...a, stock_item_id: e.target.value }))} style={{ ...sInp, width: '100%', margin: '3px 0 10px' }}>
                <option value="">— item —</option>{rows.map(r => <option key={r.item_id} value={r.item_id}>{r.name} ({num(r.qty)} {r.uom})</option>)}
              </select></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: '#555' }}>Signed qty (+ in / − out)<br /><input type="number" value={adj.qty} onChange={e => setAdj(a => ({ ...a, qty: e.target.value }))} style={{ ...sInp, width: '100%', marginTop: 3 }} /></label>
              <label style={{ fontSize: 12, color: '#555' }}>Unit cost (optional)<br /><input type="number" value={adj.unit_cost} onChange={e => setAdj(a => ({ ...a, unit_cost: e.target.value }))} style={{ ...sInp, width: '100%', marginTop: 3 }} placeholder="opening cost" /></label>
            </div>
            <label style={{ fontSize: 12, color: '#555' }}>Reason *<br /><input value={adj.reason} onChange={e => setAdj(a => ({ ...a, reason: e.target.value }))} style={{ ...sInp, width: '100%', margin: '3px 0 10px' }} /></label>
            {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{err}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setAdj(null)} style={{ ...sBtn, background: '#f0f2f8', color: '#555' }}>Cancel</button>
              <button onClick={postAdj} disabled={busy} style={{ ...sBtn, opacity: busy ? 0.5 : 1 }}>{busy ? 'Posting…' : 'Post adjustment'}</button>
            </div>
          </div>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Item', 'Store', 'On hand', 'UOM', 'Avg ₹', 'Value ₹', 'Reorder', 'Status'].map(h => <th key={h} style={sTh}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} style={{ ...sTd, textAlign: 'center', color: '#aaa' }}>No stock items yet.</td></tr>}
          {rows.map(r => (
            <tr key={r.item_id}>
              <td style={sTd}>{r.name} <span style={{ color: '#aaa', fontSize: 11 }}>{r.code}</span></td>
              <td style={sTd}>{r.store}</td>
              <td style={{ ...sTd, fontWeight: 700, textAlign: 'right' }}>{num(r.qty).toLocaleString('en-IN')}</td>
              <td style={sTd}>{r.uom}</td>
              <td style={{ ...sTd, textAlign: 'right' }}>{r.avg_rate != null ? num(r.avg_rate).toFixed(2) : '—'}</td>
              <td style={{ ...sTd, textAlign: 'right' }}>{num(r.value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
              <td style={{ ...sTd, textAlign: 'right', color: '#aaa' }}>{num(r.reorder_level)}</td>
              <td style={sTd}><span style={{ fontWeight: 700, color: r.status === 'out' ? '#B91C1C' : r.status === 'low' ? '#B45309' : '#166534' }}>{r.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GpoConsumption() {
  const [rows, setRows] = React.useState([]);
  const [gpo, setGpo] = React.useState('');
  const load = React.useCallback(() => { api.get('/store/gpo-consumption' + (gpo.trim() ? '?gpo_no=' + encodeURIComponent(gpo.trim()) : '')).then(r => setRows(r.data.data || [])).catch(() => {}); }, [gpo]);
  React.useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <input value={gpo} onChange={e => setGpo(e.target.value)} placeholder="Filter by GPO no…" style={{ ...sInp, width: 200 }} />
        <span style={{ fontSize: 12, color: '#888' }}>Consumption vs estimate (cpp × order qty). “—” where the GPO has no estimate linkage.</span>
      </div>
      {(() => {
        const materials = rows.filter(r => r.item_master_type !== 'stock');
        const general = rows.filter(r => r.item_master_type === 'stock');
        const renderRow = (r, i) => {
          const variance = r.est_qty != null ? num(r.net_consumed) - num(r.est_qty) : null;
          return (
            <tr key={r.item_master_type + '-' + i}>
              <td style={sTd}>{r.gpo_no}</td>
              <td style={sTd}>{r.item_name}</td>
              <td style={{ ...sTd, fontSize: 11, color: '#888' }}>{TYPE_LABEL[r.item_master_type] || r.item_master_type}</td>
              <td style={{ ...sTd, textAlign: 'right' }}>{num(r.issued_qty)}</td>
              <td style={{ ...sTd, textAlign: 'right' }}>{num(r.returned_qty)}</td>
              <td style={{ ...sTd, textAlign: 'right', fontWeight: 700 }}>{num(r.net_consumed)}</td>
              <td style={{ ...sTd, textAlign: 'right' }}>{r.est_qty != null ? num(r.est_qty) : '—'}</td>
              <td style={{ ...sTd, textAlign: 'right', color: variance == null ? '#aaa' : variance > 0 ? '#B91C1C' : '#166534' }}>{variance == null ? '—' : (variance > 0 ? '+' : '') + variance.toFixed(2)}</td>
            </tr>
          );
        };
        const secHdr = (label, note) => <tr><td colSpan={8} style={{ padding: '8px 10px', background: '#F7F6F3', fontSize: 11, fontWeight: 700, color: '#3B3A8F' }}>{label} <span style={{ fontWeight: 400, color: '#B45309' }}>{note}</span></td></tr>;
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['GPO', 'Item', 'Kind', 'Issued', 'Returned', 'Net', 'Est', 'Variance'].map(h => <th key={h} style={sTh}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} style={{ ...sTd, textAlign: 'center', color: '#aaa' }}>No GPO-linked issues yet.</td></tr>}
              {materials.length > 0 && secHdr('MATERIAL CONSUMPTION (fabric / trims / yarn)', '· consumption records — document-only, no stock balance')}
              {materials.map(renderRow)}
              {general.length > 0 && secHdr('GENERAL STORE (stock-tracked)', '')}
              {general.map(renderRow)}
            </tbody>
          </table>
        );
      })()}
    </div>
  );
}

const TYPE_LABEL = { stock: 'General (stock)', trims: 'Trims', fabric: 'Fabric', yarn: 'Yarn' };
// Typeahead across all four masters (standing rule: identity fields reference masters). Grouped by type.
function LinePicker({ value, onPick }) {
  const [q, setQ] = React.useState(value || '');
  const [res, setRes] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setQ(value || ''); }, [value]);
  const search = (v) => {
    setQ(v); setOpen(true);
    if (v.trim().length < 1) { setRes([]); return; }
    api.get('/procurement/item-masters?q=' + encodeURIComponent(v.trim())).then(r => setRes(r.data.data || [])).catch(() => setRes([]));
  };
  const grouped = ['stock', 'trims', 'fabric', 'yarn'].map(t => [t, res.filter(x => x.type === t)]).filter(([, a]) => a.length);
  return (
    <div style={{ position: 'relative' }}>
      <input value={q} onChange={e => search(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="search all masters…" style={{ ...sInp, width: '100%' }} />
      {open && grouped.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 10, background: '#fff', border: '1px solid #ddd', borderRadius: 6, width: '100%', maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {grouped.map(([t, arr]) => (
            <div key={t}>
              <div style={{ padding: '3px 8px', fontSize: 10, fontWeight: 700, color: '#888', background: '#f4f4f8', textTransform: 'uppercase' }}>{TYPE_LABEL[t]}</div>
              {arr.map(x => (
                <div key={t + x.id} onMouseDown={() => { onPick({ item_master_type: x.type, item_master_id: x.id, name: x.name, uom: x.uom }); setQ(x.name); setOpen(false); }}
                  style={{ padding: '6px 8px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}>{x.name} <span style={{ color: '#aaa', fontSize: 11 }}>{x.uom}</span></div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IssueForm({ onClose, onSaved }) {
  const [depts, setDepts] = React.useState([]);
  const [dept, setDept] = React.useState('');
  const [gpo, setGpo] = React.useState('');
  const [remarks, setRemarks] = React.useState('');
  const [lines, setLines] = React.useState([{ item_master_type: '', item_master_id: '', name: '', uom: '', qty: '' }]);
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { api.get('/store/departments').then(r => setDepts(r.data.data || [])).catch(() => {}); }, []);
  const setLine = (i, patch) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  const save = async () => {
    setErr('');
    if (!dept) return setErr('A department is required.');
    const valid = lines.filter(l => l.item_master_id && num(l.qty) > 0);
    if (!valid.length) return setErr('Add at least one item with a quantity.');
    setBusy(true);
    try {
      await api.post('/store/issues', { issued_to_department_id: dept, gpo_no: gpo || null, remarks: remarks || null,
        lines: valid.map(l => ({ item_master_type: l.item_master_type, item_master_id: l.item_master_id,
          stock_item_id: l.item_master_type === 'stock' ? l.item_master_id : undefined, qty: num(l.qty), uom: l.uom })) });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 760, padding: 22, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <b style={{ fontSize: 16, color: '#1a1a2e' }}>New Store Issue</b>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#555' }}>Department *<br />
            <select value={dept} onChange={e => setDept(e.target.value)} style={{ ...sInp, width: '100%', marginTop: 3 }}>
              <option value="">— select —</option>{depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></label>
          <label style={{ fontSize: 12, color: '#555' }}>GPO (optional)<br />
            <input value={gpo} onChange={e => setGpo(e.target.value)} style={{ ...sInp, width: '100%', marginTop: 3 }} placeholder="e.g. 1771" /></label>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, tableLayout: 'fixed' }}>
          <thead><tr>{['Item (any master)', 'Kind', 'Qty', ''].map((h, k) => <th key={h} style={{ ...sTh, width: k === 0 ? '46%' : k === 1 ? '34%' : k === 2 ? '14%' : '6%' }}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={{ ...sTd, verticalAlign: 'top' }}><LinePicker value={l.name} onPick={p => setLine(i, p)} /></td>
                <td style={{ ...sTd, verticalAlign: 'top' }}>{!l.item_master_type ? <span style={{ color: '#bbb' }}>—</span> : l.item_master_type === 'stock'
                  ? <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ stock-tracked</span>
                  : <span style={{ fontSize: 10, color: '#B45309', background: '#FFF4E5', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }} title="Recorded on the slip for consumption / GPO reporting only — no on-hand balance is kept for this item (ruling #5).">📄 consumption record — not stock-tracked</span>}</td>
                <td style={{ ...sTd, verticalAlign: 'top' }}><input type="number" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} style={{ ...sInp, width: '100%', textAlign: 'right' }} placeholder={l.uom} /></td>
                <td style={{ ...sTd, verticalAlign: 'top' }}>{lines.length > 1 && <button onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#B91C1C', cursor: 'pointer' }}>✕</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={() => setLines(ls => [...ls, { item_master_type: '', item_master_id: '', name: '', uom: '', qty: '' }])} style={{ ...sBtn, background: '#eef', color: '#3B3A8F', marginBottom: 12 }}>+ line</button>
        <input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Purpose / remarks" style={{ ...sInp, width: '100%', marginBottom: 12 }} />
        {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ ...sBtn, background: '#f0f2f8', color: '#555' }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ ...sBtn, opacity: busy ? 0.5 : 1 }}>{busy ? 'Saving…' : 'Save draft'}</button>
        </div>
      </div>
    </div>
  );
}

function IssueSlips() {
  const [rows, setRows] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const load = React.useCallback(() => { api.get('/store/issues').then(r => setRows(r.data.data || [])).catch(() => {}); }, []);
  React.useEffect(() => { load(); }, [load]);
  const act = async (id, verb, body) => {
    try { await api.post(`/store/issues/${id}/${verb}`, body || {}); setMsg(`${verb} done`); setTimeout(() => setMsg(''), 2000); load(); }
    catch (e) { alert(e.response?.data?.error || `${verb} failed`); }
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#888' }}>Raise a slip (draft) → Issue (posts stock) → Acknowledge (receiver's name) → Close. Corrections via Returns.</span>
        <div>{msg && <span style={{ color: '#166534', fontSize: 12, marginRight: 10 }}>✓ {msg}</span>}<button onClick={() => setCreating(true)} style={sBtn}>+ New Issue</button></div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['IS No', 'Department', 'GPO', 'Lines', 'Status', 'Received by', 'Actions'].map(h => <th key={h} style={sTh}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} style={{ ...sTd, textAlign: 'center', color: '#aaa' }}>No issues yet.</td></tr>}
          {rows.map(r => (
            <tr key={r.id}>
              <td style={{ ...sTd, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.is_no}</td>
              <td style={sTd}>{r.department_name}</td>
              <td style={sTd}>{r.gpo_no || '—'}</td>
              <td style={{ ...sTd, textAlign: 'center' }}>{r.line_count}</td>
              <td style={sTd}>{statusPill(r.status)}</td>
              <td style={sTd}>{r.received_by_name || '—'}</td>
              <td style={sTd}>
                {r.status === 'draft' && <button onClick={() => act(r.id, 'issue')} style={{ ...sBtn, padding: '4px 10px', fontSize: 12 }}>Issue</button>}
                {r.status === 'issued' && <button onClick={() => { const n = prompt('Received by (name on slip):'); if (n) act(r.id, 'acknowledge', { received_by_name: n }); }} style={{ ...sBtn, padding: '4px 10px', fontSize: 12, background: '#166534' }}>Acknowledge</button>}
                {['issued', 'acknowledged', 'partially_returned'].includes(r.status) && <button onClick={() => act(r.id, 'close')} style={{ ...sBtn, padding: '4px 10px', fontSize: 12, background: '#555', marginLeft: 6 }}>Close</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <IssueForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

// ─── Rejections (mig 203) ────────────────────────────────────────────────────
// The register of what we rejected and what became of it. Until this screen, rejected_qty
// reached a human in two places only — a column inside Awaiting QC, which by its own filter
// can show receipt-time rejections and never a QC one, and a column in the GRC queue's
// expanded lines. Neither is a register. The nearest thing, QC Failed → Return to Vendor,
// showed NOTHING: it needs quality_status='failed' AND an open RTV milestone, and no RTV
// milestone has ever been created.
//
// DISPOSITION AND REPLACEMENT ARE INDEPENDENT. A row can be fully replaced and still have
// goods on the floor, so undisposed and unreplaced are separate columns, never summed.
//
// SCRAPPED IS HIDDEN, NOT REFUSED. The backend 403s a store user who tries, but a button
// that fails teaches people the system is broken. Same shape as the isGate short-circuit at
// the top of this file: if the role cannot do it, it is not there.
function Rejections({ user }) {
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const [err, setErr] = React.useState('');

  const canAct   = ['admin', 'management', 'stores_incharge', 'store'].includes(user?.role);
  const canScrap = ['admin', 'management'].includes(user?.role);

  const load = React.useCallback(() => {
    api.get('/procurement/rejections').then(r => setRows(r.data.data || [])).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const th = { padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
  const qty = (v) => num(v).toLocaleString('en-IN', { maximumFractionDigits: 3 });
  // The gate-pass ladder rungs reused, not a second cadence: amber past a week, red past a fortnight.
  const ageColour = (d) => d == null ? '#aaa' : d >= 14 ? '#A32D2D' : d >= 7 ? '#B45309' : '#555';

  const onClock   = rows.filter(r => r.status === 'on_clock');
  const noVerdict = rows.filter(r => r.status === 'no_verdict');

  const openModal = (row, mode) => {
    setErr('');
    setModal({ row, mode, kind: 'returned',
      qty: String(num(mode === 'replacement' ? row.unreplaced_qty : row.undisposed_qty)),
      date: '',   // stated, never prefilled (dates change 2)
      gate_entry_no: '', challan_no: '', reason: '', remarks: '' });
  };

  const save = async () => {
    const m = modal;
    if (!(num(m.qty) > 0)) { setErr('Quantity must be more than zero.'); return; }
    if (m.mode === 'disposition' && m.kind === 'scrapped' && !m.reason.trim()) { setErr('A reason is required to scrap.'); return; }
    setBusy(m.row.grn_id); setErr('');
    try {
      if (m.mode === 'replacement') {
        await api.post('/procurement/grn/' + m.row.grn_id + '/replacement', {
          qty: num(m.qty), received_date: m.date || null,
          challan_no: m.challan_no.trim() || null, gate_entry_no: m.gate_entry_no.trim() || null,
          remarks: m.remarks.trim() || null });
      } else {
        await api.post('/procurement/grn/' + m.row.grn_id + '/disposition', {
          kind: m.kind, qty: num(m.qty), disposed_at: m.date || null,
          gate_entry_no: m.kind === 'returned' ? (m.gate_entry_no.trim() || null) : null,
          reason: m.reason.trim() || null });
      }
      setModal(null); load();
    } catch (e) {
      // The ceiling 409 already reads as a sentence naming the numbers — show it verbatim.
      setErr(e.response?.data?.error || 'Could not save');
    } finally { setBusy(null); }
  };

  const section = (title, list, colour, note) => (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: colour, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{title} ({list.length})</div>
      {note && <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>{note}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['GRN', 'PO No', 'Vendor', 'Rejected', 'Undisposed', 'Unreplaced', 'Failed', 'Days', ''].map(h => (
          <th key={h} style={{ ...th, textAlign: ['Rejected', 'Undisposed', 'Unreplaced', 'Days'].includes(h) ? 'right' : 'left' }}>{h}</th>))}</tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#aaa' }}>Nothing here.</td></tr>}
          {list.map(r => (
            <tr key={r.grn_id}>
              <td style={{ ...td, fontFamily: 'monospace', color: '#A32D2D' }}>{r.grn_no}</td>
              <td style={{ ...td, fontFamily: 'monospace' }}>{r.po_no}</td>
              <td style={td}>{r.supplier || '—'}</td>
              <td style={{ ...td, textAlign: 'right' }}>{qty(r.rejected_qty)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: num(r.undisposed_qty) ? 700 : 400, color: num(r.undisposed_qty) ? '#A32D2D' : '#aaa' }}>{qty(r.undisposed_qty)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: num(r.unreplaced_qty) ? 700 : 400, color: num(r.unreplaced_qty) ? '#B45309' : '#aaa' }}>{qty(r.unreplaced_qty)}</td>
              <td style={{ ...td, color: '#888' }}>{r.failed_on ? new Date(r.failed_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: ageColour(r.days_open) }}>{r.days_open == null ? '—' : r.days_open}</td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {canAct ? (<>
                  {num(r.undisposed_qty) > 0 && (
                    <button disabled={busy === r.grn_id} onClick={() => openModal(r, 'disposition')}
                      style={{ padding: '5px 11px', background: '#A32D2D', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>↩ Disposition</button>)}
                  {num(r.unreplaced_qty) > 0 && (
                    <button disabled={busy === r.grn_id} onClick={() => openModal(r, 'replacement')}
                      style={{ padding: '5px 11px', background: '#166534', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600, marginLeft: 6 }}>📦 Replacement</button>)}
                </>) : <span style={{ fontSize: 11, color: '#aaa' }}>view only</span>}
              </td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );

  const lbl = { fontSize: 11, color: '#888', display: 'block', marginBottom: 3 };
  const inp = { width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box', fontSize: 13 };

  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
        What we rejected, and what became of it. A disposition (returned or scrapped) and a replacement
        are independent — goods can be replaced and still be sitting on the floor.
      </div>
      {/* ── WHAT IS ACTUALLY OUTSTANDING ──────────────────────────────────────────────────
          The two sections below are correct and were already here; what they could not show
          is a total, because the register splits by whether a clock exists. That split hides
          the exposure: the LARGEST rejections are the ones with no verdict to age from, so
          the amber section carries the big numbers and the red section carries the visible
          days. Stated together here, with quantity leading rather than age. */}
      {(() => {
        const open = rows.filter(r => Number(r.undisposed_qty) > 0);
        if (!open.length) return null;
        const units = open.reduce((a, r) => a + (Number(r.undisposed_qty) || 0), 0);
        const oldest = open.reduce((a, r) => Math.max(a, Number(r.days_open) || 0), 0);
        const unclocked = open.filter(r => !Number(r.days_open));
        const unclockedUnits = unclocked.reduce((a, r) => a + (Number(r.undisposed_qty) || 0), 0);
        return (
          <div style={{ marginBottom: 16, border: '1px solid #E5B4B4', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ background: '#A32D2D', color: '#fff', padding: '8px 13px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
              Rejected goods with no disposition
            </div>
            <div style={{ background: '#fff', padding: 14, display: 'flex', gap: 22, flexWrap: 'wrap' }}>
              <div><div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Receipts</div>
                   <div style={{ fontSize: 22, fontWeight: 800, color: '#A32D2D' }}>{open.length}</div></div>
              <div><div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Units</div>
                   <div style={{ fontSize: 22, fontWeight: 800, color: '#A32D2D' }}>{qty(units)}</div></div>
              <div><div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Oldest on clock</div>
                   <div style={{ fontSize: 22, fontWeight: 800, color: '#A32D2D' }}>{oldest ? `${oldest}d` : '—'}</div></div>
              {unclocked.length > 0 && (
                <div style={{ flex: 1, minWidth: 240, background: '#FEF3C7', color: '#854F0B', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.5 }}>
                  <strong>{qty(unclockedUnits)} units across {unclocked.length} receipt(s) have no clock at all.</strong>{' '}
                  Their GRN was marked passed with the rejection recorded inside, so there is no QC
                  verdict to age from — they will never turn red however long they sit.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {section('Rejections — on clock', onClock, '#A32D2D', 'Ageing from the QC verdict. Amber past a week, red past a fortnight.')}
      {section('Rejections — no verdict', noVerdict, '#B45309', 'No QC was performed on these, so there is no verdict to age from. They need a disposition recorded by hand.')}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 22, width: 440, maxWidth: '92vw' }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: '#1a1a2e' }}>{modal.mode === 'replacement' ? '📦 Record a replacement' : '↩ Record a disposition'}</div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>{modal.row.grn_no} · {modal.row.supplier || '—'} · rejected {qty(modal.row.rejected_qty)}</div>

            {modal.mode === 'disposition' && (
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>What happened to the goods</label>
                <select style={inp} value={modal.kind} onChange={e => setModal(m => ({ ...m, kind: e.target.value }))}>
                  <option value="returned">Returned to vendor</option>
                  {/* Hidden, not refused — the backend 403 is the gate, this is the courtesy. */}
                  {canScrap && <option value="scrapped">Scrapped — written off at our cost</option>}
                </select>
              </div>)}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Quantity (max {qty(modal.mode === 'replacement' ? modal.row.unreplaced_qty : modal.row.undisposed_qty)})</label>
                <input type="number" step="0.001" style={inp} value={modal.qty} onChange={e => setModal(m => ({ ...m, qty: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>{modal.mode === 'replacement' ? 'Received on' : 'Disposed on'}</label>
                <input type="date" style={inp} value={modal.date} onChange={e => setModal(m => ({ ...m, date: e.target.value }))} />
              </div>
            </div>

            {/* A scrap never crosses the gate, so it is never asked for an entry number. */}
            {(modal.mode === 'replacement' || modal.kind === 'returned') && (
              <div style={{ display: 'grid', gridTemplateColumns: modal.mode === 'replacement' ? '1fr 1fr' : '1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>Gate entry no (optional)</label>
                  <input style={inp} value={modal.gate_entry_no} placeholder="from the guard's book" onChange={e => setModal(m => ({ ...m, gate_entry_no: e.target.value }))} />
                </div>
                {modal.mode === 'replacement' && (
                  <div>
                    <label style={lbl}>Challan no (optional)</label>
                    <input style={inp} value={modal.challan_no} onChange={e => setModal(m => ({ ...m, challan_no: e.target.value }))} />
                  </div>)}
              </div>)}

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>{modal.mode === 'replacement' ? 'Remarks (optional)' : modal.kind === 'scrapped' ? 'Reason — required to write off' : 'Reason (optional)'}</label>
              <input style={inp} value={modal.mode === 'replacement' ? modal.remarks : modal.reason}
                onChange={e => setModal(m => modal.mode === 'replacement' ? { ...m, remarks: e.target.value } : { ...m, reason: e.target.value })} />
            </div>

            {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setModal(null)} style={{ padding: '8px 16px', background: '#eee', color: '#555', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button disabled={!!busy} onClick={save} style={{ padding: '8px 16px', background: modal.mode === 'replacement' ? '#166534' : '#A32D2D', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{busy ? '…' : 'Record'}</button>
            </div>
          </div>
        </div>)}
    </div>
  );
}

function StoreReturns() {
  const [rows, setRows] = React.useState([]);
  const [sel, setSel] = React.useState(null);      // { issue, lines, qtys }
  const load = React.useCallback(() => { api.get('/store/issues').then(r => setRows((r.data.data || []).filter(x => ['issued', 'acknowledged', 'partially_returned'].includes(x.status)))).catch(() => {}); }, []);
  React.useEffect(() => { load(); }, [load]);
  const open = async (id) => { const r = await api.get(`/store/issues/${id}`); setSel({ issue: r.data.data, qtys: {} }); };
  const post = async () => {
    const lines = sel.issue.lines.filter(l => num(sel.qtys[l.id]) > 0).map(l => ({ store_issue_line_id: l.id, qty: num(sel.qtys[l.id]) }));
    if (!lines.length) return alert('Enter a return qty on at least one line.');
    try { await api.post(`/store/issues/${sel.issue.id}/returns`, { lines }); setSel(null); load(); }
    catch (e) { alert(e.response?.data?.error || 'Return failed'); }
  };
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Return unused material against an issue (partial allowed). Each return is a child IS-…/Rn and re-enters stock at its issued cost.</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['IS No', 'Department', 'Status', ''].map(h => <th key={h} style={sTh}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} style={{ ...sTd, textAlign: 'center', color: '#aaa' }}>Nothing returnable.</td></tr>}
          {rows.map(r => (
            <tr key={r.id}>
              <td style={{ ...sTd, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.is_no}</td>
              <td style={sTd}>{r.department_name}</td>
              <td style={sTd}>{statusPill(r.status)}</td>
              <td style={sTd}><button onClick={() => open(r.id)} style={{ ...sBtn, padding: '4px 10px', fontSize: 12, background: '#B45309' }}>Return…</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {sel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 620, padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}><b>Return against {sel.issue.is_no}</b><button onClick={() => setSel(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead><tr>{['Item type', 'Issued', 'Returned', 'Return now'].map(h => <th key={h} style={sTh}>{h}</th>)}</tr></thead>
              <tbody>
                {sel.issue.lines.map(l => {
                  const open = num(l.qty) - num(l.qty_returned);
                  return (
                    <tr key={l.id}>
                      <td style={sTd}>{l.item_master_type} {l.uom}</td>
                      <td style={{ ...sTd, textAlign: 'right' }}>{num(l.qty)}</td>
                      <td style={{ ...sTd, textAlign: 'right' }}>{num(l.qty_returned)}</td>
                      <td style={sTd}><input type="number" max={open} disabled={open <= 0} value={sel.qtys[l.id] || ''} onChange={e => setSel(s => ({ ...s, qtys: { ...s.qtys, [l.id]: e.target.value } }))} style={{ ...sInp, width: 90, textAlign: 'right' }} placeholder={`≤ ${open}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setSel(null)} style={{ ...sBtn, background: '#f0f2f8', color: '#555' }}>Cancel</button>
              <button onClick={post} style={{ ...sBtn, background: '#B45309' }}>Post return</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
